import { type BaseTranslation } from '../../i18n-types';

const desktop_web: BaseTranslation = {
  eligiblePayers: {
    title: 'Eligible insurance payers',
    subtitle:
      "Please verify the patient's insurance coverage before submitting an eConsult.",
    payerCount: '{count:number} payer{{s}}',
  },
  activityTracker: {
    title: 'You will be logged out soon',
    description:
      'You have been inactive in almost {amountOfMinutes:number} minutes. Move your mouse to stay logged in.',
  },
  annotations: {
    annotationList: {
      noDataInTable: 'No data available',
      startAnnotating: 'Start annotating',
      tableHeader: {
        diagnosis: 'Diagnosis',
        difficulty: 'Difficulty',
        location: 'Location',
        status: 'Status',
      },
    },
    annotationsPage: {
      successfullySkippedCase: 'Successfully skipped this case',
      successfullyAnnotatedCase: 'Successfully annotated this case',
      somethingWentWrongCreatingAnnotation:
        'Could not save annotation, something went wrong.',
      leftSideContent: {
        annotations: 'Annotations',
        answers: 'Answers',
        backToList: 'Back to case list',
        diagnosis: 'Diagnosis',
        difficulty: 'Difficulty',
        fetchingFormerRaters: 'Fetching former raters',
        formerRaters: 'Former raters',
        genderInTheirAge:
          '{gender|{Female: Female, *: Male}} in {pronoun|{Male: his, Female: her, *: their}} {age:number}s',
        noFormerReviews: 'No former reviews',
      },
      roi: {
        button: 'ROI',
        title: 'ROI',
      },
      toolbar: {
        addTool: 'Add {tool|{arrow:arrows, pen:areas}}',
        annotationCriteriaLabel: 'Annotate {criteriaName:string}',
        annotations: 'Annotations',
        approve: 'Approve',
        area: 'Area{{s}}',
        arrow: 'Arrow{{s}}',
        arrowTool: 'Arrow',
        cancel: 'Cancel',
        clickToAnnotate:
          'Click on the image to start adding new {tool|{arrow:arrows, pen:areas}}',
        clinicalImage: 'Overview image',
        collapseText: 'Done',
        criteriaMultiSelect: 'Multiselect',
        criteriaPlaceholder: 'Search for other criteria',
        criteriaSearchLabel: 'Add other criteria',
        criteriaSelectLabel: 'Criteria for {diagnosisName:string}',
        dermoscopicImage: 'Dermoscopic image',
        edit: 'Edit',
        editorNoteLabel: 'Overall note',
        editorNotePlaceholder: 'Enter note here...',
        editorNotePlaceholderReadonly: 'No note',
        feedbackLabel: 'Feedback',
        feedbackNotePlaceholder: 'Enter feedback here...',
        feedbackNotePlaceholderReadonly: 'No feedback',
        markAsRead:
          '{markedAsRead|{markedAsRead:Read, notMarkedAsRead:Mark as read}}',
        penTool: 'Pen',
        requestChanges: 'Request changes',
        save: 'Save',
        skip: 'Skip',
        title: 'Progress',
        unApprove: 'Unapprove',
      },
    },
    tags: {
      annotated: 'Annotated',
      approved: 'Approved',
      changesRequested: 'Changes requested',
      disapproved: 'Disapproved',
      newFeedback: 'New feedback',
      pending: 'Pending',
      skipped: 'Skipped',
    },
  },
  administration: {
    navigationSidebar: {
      users: 'Users',
      eConsults: 'eConsults',
      title: 'Administration',
      specialists: 'Specialists',
      sites: 'Clinical sites',
      networks: 'Networks',
      federationSettings: 'Federation settings',
      forms: 'Forms',
      specialities: 'Specialities',
      announcements: 'Announcements',
      consents: 'Consents',
      globalLabel: 'Global',
    },
    federationSelector: {
      chooserTitle: 'Select a federation',
      searchPlaceholder: 'Search federations',
      noFederations: 'No federations found',
      noSearchMatches: 'No federations match your search',
      loadError: 'Could not load federations',
      retry: 'Retry',
    },
  },
  formsPage: {
    title: 'Form templates',
    slots: {
      'oral-triage': 'Oral lesion — triage',
      'oral-tele-assessment': 'Oral lesion — specialist assessment',
      'cervical-triage': 'Cervical condition — triage',
      'cervical-tele-assessment': 'Cervical condition — specialist assessment',
      'shared-response-review':
        'Oral lesion and cervical condition — response review',
    },
    columns: {
      form: 'Form',
      version: 'Version',
      formLanguageVersion: 'Form-language version',
      lastPublished: 'Last published',
      updater: 'Updated by',
      actions: 'Actions',
    },
    notPublishedYet: 'Not published yet',
    noTemplates: 'No registered form templates',
    upload: 'Upload JSON',
    uploadFor: 'Upload JSON for {form:string}',
    confirm: {
      title: 'Overwrite the published template?',
      description:
        'This replaces the currently published JSON for {form:string}.',
      action: 'Overwrite template',
    },
    publishSuccess: 'Published {form:string} successfully.',
    loadError: 'Could not load form templates. Try again.',
    errors: {
      malformedJson: 'The selected file is not valid JSON: {message:string}',
      validationFailed: 'The template failed validation:',
      requestTooLarge:
        'The serialized upload request is too large. Keep it below 100 kB.',
      readFailed: 'Could not read the selected file.',
      publishFailed: 'Could not publish the form template. Try again.',
      alreadyCreated:
        'Somebody published this form first. The list has been refreshed — upload again to publish a new version.',
      assignedElsewhere:
        'Published, but another template is already assigned to one of its condition types for this form, so that one was left as it is.',
      assignFailed:
        'Published, but it could not be assigned to every condition type. Upload again to retry.',
    },
  },
  announcementsPage: {
    title: 'Announcements',
    description:
      'A user sees one announcement at a time: critical first, then warning, info and success. The newest wins a tie.',
    create: 'Create announcement',
    noData: 'No announcements yet',
    noMatches: 'No announcements match these filters',
    noDataDescription:
      'Announcements you create for this federation are listed here.',
    loadError: 'The announcements could not be loaded.',
    columns: {
      title: 'Title',
      type: 'Type',
      status: 'Status',
      audience: 'Audience',
      statistics: 'Statistics',
      period: 'Period (UTC)',
    },
    allPages: 'All pages',
    types: {
      info: 'Info',
      warning: 'Warning',
      success: 'Success',
      critical: 'Critical',
    },
    statuses: {
      draft: 'Draft',
      scheduled: 'Scheduled',
      active: 'Active',
      expired: 'Expired',
    },
    shadowed:
      'Shadowed by “{title:string}” for an overlapping audience, {period:string} (UTC)',
    audience: {
      allUsers: 'All users',
      federationManager: 'Federation Manager',
      roles: '{count:number} role{{s}}',
      jurisdictionalGroups: '{count:number} network{{s}}',
      medicalUnits: '{count:number} clinical site{{s}}',
      rolesIn: '{roles:string} in {scopes:string}',
    },
    statistics: {
      estimatedReach: 'est. reach',
      impressions: 'Impressions',
      uniqueUsers: 'Users',
      dismissals: 'Dismissed',
      linkOpens: 'Link opens',
      note: 'Counts only users with product analytics on.',
    },
    period: {
      untilRemoved: 'Until removed',
      from: 'From {date:string}',
      to: 'To {date:string}',
    },
    actions: {
      edit: 'Edit',
      duplicate: 'Duplicate',
      preview: 'Full preview',
      delete: 'Delete',
    },
    filters: {
      clear: 'Clear',
      selected: 'Selected',
      all: 'All',
    },
    deleteDialog: {
      title: 'Delete announcement?',
      description:
        '“{title:string}” is removed from this list and from every user’s banner.',
      confirm: 'Delete',
      deleted: 'Announcement deleted',
      failed: 'The announcement could not be deleted.',
    },
    preview: {
      title: 'Full preview',
      description: 'How users see this announcement at the top of every page.',
    },
    sheet: {
      createTitle: 'Create announcement',
      editTitle: 'Edit announcement',
      steps: {
        target: 'Target',
        audience: 'Audience',
        content: 'Content',
        review: 'Review',
      },
      federations: {
        label: 'Federations',
        selectAll: 'Select all',
        helper: 'Each selected federation gets its own announcement.',
        readOnly: 'An announcement stays in the federation it was created for.',
      },
      audience: {
        everyone: 'Everyone',
        narrow: 'Narrow by roles and places',
        allRoles: 'All roles',
        roles: 'Roles',
        scopes: 'Where',
        scopesHelper: 'Nothing selected means the whole federation.',
        jurisdictionalGroups: 'Networks',
        medicalUnits: 'Clinical sites',
        searchJurisdictionalGroups: 'Search networks',
        searchMedicalUnits: 'Search clinical sites',
        removedScope: 'No longer exists',
        loadError:
          'The networks and sites of this federation could not be loaded.',
      },
      content: {
        type: 'Type',
        typeDescriptions: {
          info: 'General information.',
          warning: 'Something users should plan around.',
          success: 'Something that went well or is resolved.',
          critical: 'An active clinical or system risk.',
        },
        typeHelper: 'Sets the colour and whether users can dismiss it.',
        dismissible: 'Dismissible',
        minimiseOnly: 'Minimise only',
        language: 'Content in {language:string}',
        languageHelper: 'For {federations:string}.',
        title: 'Title',
        body: 'Message',
        linkLabel: 'Link text',
        linkUrl: 'Link URL',
        linkHelper: 'Optional. A link needs both a text and a URL.',
        linkLabelCount: '{count:number}/{max:number}',
        preview: 'Preview',
        previewNote: 'This is how the announcement will appear once published.',
      },
      review: {
        schedule: 'Schedule',
        startsAt: 'Active from (UTC)',
        endsAt: 'Active until (UTC)',
        date: 'Date',
        time: 'Time',
        pickDate: 'Pick a date',
        untilRemoved: 'Until removed (no end date)',
        summary: '{count:number} separate announcement{{s}} will be created:',
        summaryEdit: 'This announcement belongs to {federation:string}.',
        independent: 'Each is edited, deleted and counted on its own.',
      },
      errors: {
        federations: 'Select at least one federation.',
        removedScope:
          'Untick the networks and clinical sites that no longer exist.',
        narrowEmpty: 'Pick at least one role, network or clinical site.',
        required: 'Required.',
        plainText: 'Use plain text, without HTML tags.',
        linkPair: 'A link needs both a text and a URL.',
        linkUrl: 'Enter a full web address, such as https://example.com',
        tooLong: 'At most {max:number} characters.',
        endAfterStart: 'The end must be after the start.',
        endInPast: 'The end has already passed.',
      },
      back: 'Back',
      next: 'Continue',
      save: 'Save',
      publish: 'Publish {count:number} announcement{{s}}',
      saveDraft: 'Save draft',
      created: '{count:number} announcement{{s}} created',
      saved: 'Announcement saved',
      failed: 'The announcement could not be saved.',
    },
  },
  specialitiesPage: {
    iconMissingFromWebKit:
      'Missing from the web icon kit, so it would not render on desktop. Upload it to that Font Awesome kit first.',
    iconMissingFromMobileKit:
      'Missing from the mobile icon kit, so it would not render in the Capture app. Upload it to that Font Awesome kit and release the apps first.',
    back: 'Back',
    info: 'Speciality',
    editConditionType: 'Edit {name:string}',
    conditionTypeNameLocked:
      'The English name identifies this condition type to the apps and cannot be changed here.',
    conditionTypeCreated: 'Condition type created.',
    conditionTypeUpdated: 'Condition type updated.',
    noConditionTypes: 'No condition types yet',
    notVisibleYet:
      'This speciality is not visible in the apps until it has at least one condition type.',
    title: 'Specialities',
    addSpeciality: 'Add speciality',
    editSpeciality: 'Edit speciality',
    noSpecialities: 'No specialities yet',
    loadError: 'Could not load specialities. Try again.',
    conditionTypesLoadError: 'Could not load condition types. Try again.',
    noIcon: 'No icon',
    positionBefore: '{position:number} — before {item:string}',
    positionLast: '{position:number} — last',
    positionPushesDown: '{item:string} and everything below it moves down one.',
    positionAtEnd: 'Added at the end of the list.',
    conditionTypes: 'Condition types',
    addConditionType: 'Add condition type',
    createSuccess: 'Speciality created.',
    updateSuccess: 'Speciality updated.',
    columns: {
      icon: 'Icon',
      name: 'Name',
      nameDa: 'Danish name',
      nameDe: 'German name',
      conditionTypes: 'Condition types',
      ordering: 'List position',
    },
    fields: {
      name: 'Name (English)',
      nameDa: 'Name (Danish)',
      nameDe: 'Name (German)',
      icon: 'Icon',
      iconPlaceholder: 'Select an icon',
      ordering: 'List position',
    },
    errors: {
      nameRequired: 'Enter a name',
      conditionTypeNameRequired: 'Enter a condition type name',
      duplicateName: 'That name is already taken.',
      saveFailed: 'Could not save the speciality. Try again.',
      conditionTypeSaveFailed: 'Could not save the condition type. Try again.',
    },
  },
  federationPage: {
    pageTitle: 'Settings',
    loadError: 'Could not load the federation settings.',
    generalSettings: {
      title: 'General settings',
      locale: 'Locale',
      localeDescription: 'Sets the default language for this federation.',
      localePlaceholder: 'Select locale',
      localeOptions: {
        en: 'English',
        da: 'Danish',
        de: 'German',
      },
      requireManualRelease: 'Activates manual release of eConsults',
      requireManualReleaseDescription:
        'When enabled, eConsults must be manually released by insurance managers, before being sent to a specialist.',
      reassignmentHours: 'Referral reassignment hours (h)',
      reassignmentHoursDescription:
        'Number of hours before an unanswered referral is automatically assigned to another specialist.',
      reassignmentHoursPlaceholder: 'E.g. 12',
      referralNotificationEmail: 'Referral notification email',
      referralNotificationEmailDescription:
        'Email address that receives a notification whenever a new eConsult is created in this federation.',
      referralNotificationEmailPlaceholder: 'email@example.com',
      claimReferral: 'Claim referral',
      claimReferralDescription:
        'Allows specialists to claim an eConsult before responding. Can be used when multiple specialists share a clinical site, preventing two specialists from responding to the same case.',
      hideEconsultIdentifier: 'Hide eConsult identifier',
      hideEconsultIdentifierDescription:
        'Hides the eConsult identifier from the user interface.',
      imageQualityAssessment: 'Image quality assessment',
      imageQualityAssessmentDescription:
        'Enables image quality assessment, allowing the specialist to evaluate the quality of images captured by the referring clinician.',
      enableEconsultNotifications: 'Enable eConsult notifications',
      enableEconsultNotificationsDescription:
        'Enables or disables email notifications to PCPs when a new eConsult has been assessed by a specialist.',
      econsultQuestionSummary: 'eConsult question and/or case summary',
      econsultQuestionSummaryDescription:
        'Sets whether the free-text question or case summary field is mandatory for the PCP to fill out when creating an eConsult.',
      lesions: {
        title: 'Lesions',
        description:
          'Lesions always enabled as default. This flow cannot be turned off.',
      },
      rashes: {
        title: 'Rashes',
        description:
          'Enable this to allow users to register rashes and access the full rash workflow in Dermloop.',
      },
      priorSpecialistAssignmentLookbackDays:
        'Prior specialist assignment lookback days',
      priorSpecialistAssignmentLookbackDaysDescription:
        'Number of days to look back when a new referral is created for a patient. The system checks which specialist responded to previous cases for this patient within the lookback period, ensuring continuity of care by assigning the same specialist.',
      userAffiliationOccupationAndSpecialisation:
        'Enable occupation and specialisation',
      userAffiliationOccupationAndSpecialisationDescription:
        "Shows the user's occupation and specialisation in the patient medical record and, in some cases, in the right sidebar.",
      showDatetimeOnTeleEvaluation:
        'Show date and time of teledermatologist evaluation',
      showDatetimeOnTeleEvaluationDescription:
        "Displays the date and time of the teledermatologist's evaluation to the referring clinician.",
      enableTeleAssessmentRating:
        'Enable rating for the specialists assessment',
      enableTeleAssessmentRatingDescription:
        "Allows clinicians to anonymously rate the quality of a specialist's assessment. Ratings will be visible to the federation manager.",
      timeSpentReviewingTeleassessments:
        'Enable time spent reviewing tele assessments',
      timeSpentReviewingTeleassessmentsDescription:
        'Prompts specialists to log time spent reviewing each assessment.',
      federationNameInPdfTitle: 'Enable federation name in PDF title',
      federationNameInPdfTitleDescription:
        "Includes the federation name in the title of PDF reports generated from a patient's medical record.",
      inactivityDeactivationDays: 'Inactivity deactivation days',
      inactivityDeactivationDaysDescription:
        'Number of days of inactivity before a user is deactivated.',
    },
    patientDataSettings: {
      title: 'Patient data settings',
      patientIdentifier: 'Patient identifier',
      patientIdentifierDescription:
        'Defines which type of patient identifier is used in this federation.',
      patientIdentifierPlaceholder: 'Select identifier',
      patientIdentifierOptions: {
        ssn: 'Social security number',
        ssnDescription:
          'Uses the social security number as the primary patient identifier.',
        mrn: 'Medical record number',
        mrnDescription:
          'Uses the medical record number as the primary patient identifier.',
      },
      hidePatientIdentifier: 'Hide patient identifier',
      hidePatientIdentifierDescription:
        'Hides the patient identifier from the user interface across all views.',
      hidePatientName: 'Hide patient name',
      hidePatientNameDescription:
        'Hides the patient name from the user interface across all views.',
      patientBlockPolicy: 'Patient block policy',
      patientBlockPolicyDescription:
        "Controls how patient data from outside a user's clinical site is handled.",
      patientBlockPolicyOptions: {
        noBlock: 'No block',
        noBlockDescription:
          'Users can access patient records outside their site without any restrictions.',
        softBlock: 'Soft block',
        softBlockDescription:
          'Users are shown a warning when attempting to access patient records outside their site, but access is still allowed.',
        block: 'Block',
        blockDescription:
          'Users are prevented from accessing patient records outside their site.',
      },
      patientAddressCapture: 'Patient address capture',
      patientAddressCaptureDescription:
        "Prompts for the patient's address and email during scanner registration when missing.",
      skinCheckHistoryQuestions: 'Skin-check history questions',
      skinCheckHistoryQuestionsDescription:
        'Asks two mandatory lesion questions in capture about the patient’s planned and previous skin checks with a doctor.',
    },
    save: 'Save',
    federationName: 'Name',
    federationNameDescription: 'The display name of this federation.',
    features: {
      title: 'Features',
      productAnalytics: 'Product analytics',
      productAnalyticsDescription:
        'Sends product analytics events from the signed-in apps for everyone in this federation.',
      enabledElsewhere:
        'Product analytics is also turned on for some or all of this federation somewhere else. Switching this off does not turn that off.',
      loadError: 'Could not load the features.',
      toasts: {
        saved: 'Product analytics saved',
        error: 'Could not save product analytics',
      },
    },
    toasts: {
      saved: 'Federation settings saved',
      error: 'Failed to save federation settings',
    },
  },
  buttons: {
    addAdditionalCriteria: 'Add additional criteria',
    approve: 'Approve',
    backToCaseList: 'Back to case list',
    clinicalImages: 'Overview images',
    close: 'Close',
    closeUpImages: 'Close-up images',
    compare: 'Compare',
    compareImages: 'Compare images',
    copy: 'Copy',
    dermoscopicImages: 'Dermoscopic images',
    dismiss: 'Finish',
    editOrTakeAction: 'Edit/Take action',
    inviteUser: 'Invite user',
    lesionInfo: 'Lesion info',
    login: 'Log in',
    loginAsAnotherUser: 'Log in as another user',
    next: 'Next',
    nextCase: 'Next case',
    open: 'Open',
    overviewImages: 'Overview images',
    patientInfo: 'Patient info',
    previous: 'Previous',
    refresh: 'Refresh',
    remove: 'Remove',
    removeEmail: 'Remove {email:string}',
    resetFilters: 'Reset filters',
    saveChangesAndClose: 'Save and close',
    sendInvitation: 'Send invitation',
    sendTeleRequest: 'Submit eConsult',
    startAnnotating: 'Start annotating',
    unapprove: 'Unapprove',
    updateProfile: 'Update profile',
  },
  calendar: {
    rangeFromLabel: 'From',
    rangeToLabel: 'to',
  },
  changeRegionAlertDialog: {
    changeRegion: 'Change Region',
    changeToEUMessage:
      "It looks like you're in Europe, even though you're currently using our US website. Would you like to change to Dermloop EU?",
    changeToUSMessage:
      "It looks like you're in the US, even though you're currently using our EU website. Would you like to change to Dermloop US?",
    stayInEU: 'Stay in EU',
    stayInUS: 'Stay in US',
    changeToEU: 'Change to EU',
    changeToUS: 'Change to US',
  },
  clinicalActions: {
    addComment: 'Add comment',
    allowedClinicalActions: 'Allowed clinical actions',
    otherClinicalAction: 'Other clinical action',
    requestNewImage: 'Request new image',
    suggestedClinicalAction: 'Suggested clinical action',
    rateFollowUpPriority: 'Follow-up Priority',
    followUpPriorityTooltip:
      "Select the follow-up priority that best reflects how quickly the patient should be seen by their primary care provider for further evaluation or treatment. Your selection determines how the discharge summary is marked in the primary care provider's EHR.",
    followUpPriorityNoneLabel: 'None',
    accessLevels: {
      basic: 'Basic access (only Teledermatology)',
      limited:
        'Regular access (Teledermatology, Follow-up, No further action, New Image registration)',
      full: 'Full access (all actions incl. Biopsy, Planned biopsy, Topical treatment)',
    },
  },
  clinicalStatistics: {
    action: 'Action',
    average: '{medicalUnitName:string} average',
    clinicalAction: 'Clinical action',
    clinicalActions:
      '{type|{referralPhysical: Physical referrals, referralTele: Regular tele-referrals, referralTeleUrgent: Urgent tele-referrals, biopsy: Biopsies, plannedBiopsy: Planned biopsies, followUp: Follow-ups, dismissal: No further action, newImageRegistration: New image registration, topicalTreatment: Topical treatment, noAction: No action}}',
    clinicalStatistics: 'Clinical statistics',
    diagnosticAccuracy: 'Diagnostic accuracy',
    diagnosticValues: 'Diagnostic values',
    distributionOfClinicalActions:
      'The distribution of clinical actions you have chosen for your patients using Dermloop, compared to others',
    falseNegative: 'False negative',
    falsePositive: 'False positive',
    forAllTime: 'All time',
    last12Months: 'Last 12 months',
    last4Weeks: 'Last 4 weeks',
    last7Days: 'Last 7 days',
    negativePredictiveValue: 'Negative predictive value',
    notEnoughData: 'Not enough users to show data anonymously',
    performanceRelatingTo: ' ',
    positivePredictiveValue: 'Positive predictive value',
    sensitivity: 'Sensitivity',
    specificity: 'Specificity',
    toolTips: {
      diagnosticAccuracy:
        'How often one classifies the lesions correctly as benign and malignant',
      falseNegative:
        'The number of malignant lesions you incorrectly classified as benign',
      falsePositive:
        'The number of benign lesions you incorrectly classified as malignant',
      negativePredictiveValue:
        'How many of the lesions classified as benign were truly benign',
      positivePredictiveValue:
        'How many of the lesions classified as malignant were truly malignant',
      sensitivity:
        'How many of the malignant lesions were classified as malignant',
      specificity: 'How many of the benign lesions were classified as benign',
      totalLesions:
        'Only lesions registered by you, where a final answer is registered in Dermloop (dismissals, teleassesments, and biopsies), are shown here.',
      trueNegative:
        'The number of benign lesions you correctly classified as benign',
      truePositive:
        'The number of malignant lesions you correctly classified as malignant',
    },
    totalLesions: 'Total lesions',
    trainingStatistics: 'Training statistics',
    trueNegative: 'True negative',
    truePositive: 'True positive',
    you: 'You',
    yourAbilityToClassifyLesions:
      'Your ability to classify lesions as benign or malignant. Only data for patients registered in Dermloop is shown',
    yourMedicalUnitDistribution: 'Your site distribution',
    yourPersonalMetrics: 'Your personal metrics',
  },
  compareImages: {
    header: {
      back: 'Back to patient',
      title: 'Compare images',
      nImages: '{count:number} image{{count:s}}',
    },
    images: 'image{{nrOfImages:s}}',
    imageType:
      '{imageType|{Overview: Overview, Close up: Close-up, Clinical: Clinical, Follow-up dermoscopy: Follow-up dermoscopy, Dermoscopy: Dermoscopy}}',
    allImageTypes: 'All image types',
    dragAndDrop: 'Drag and drop to display image',
    gallery: {
      title: 'Gallery',
      filters: 'Filters',
      close: 'Close',
      open: 'Open',
      sortBy: 'Sort by',
      sortMostRecentFirst: 'Most recent first',
      sortOldestFirst: 'Oldest first',
      dateOfRegistration: 'Date of registration',
      allDates: 'All',
    },
    lesionFilter: {
      selectedText: 'Selected conditions',
      availableItemsText: 'All conditions',
      clearText: 'Clear',
    },
    toolbar: {
      rotate: 'Rotate',
      done: 'Done',
    },
  },
  imageOverview: {
    flashOff: 'Show with flash',
    flashOn: 'Show without flash',
    showMarker: 'Show marker',
    hideMarker: 'Hide marker',
    downloadImage: 'Download image',
    nImages: '{count:number} image{{count:s}}',
  },
  consentDialog: {
    dialogTitle: 'Missing consent',
    dialogDescription:
      'This is a list of consents you need to provide. Provide consent by checking the consent and clicking save. The links lead to the full text of the consent.',
    dialogConsentChosen: 'Sign chosen',
    dialogConsentAll: 'Sign all',
    dialogNameColumn: 'Name',
    dialogLinkColumn: 'Link',
    dialogLinkText: 'Link to consent',
    consentError: 'Please select and sign mandatory consents',
  },
  consent: {
    consentList: {
      loadError: 'The consents could not be loaded. Try again.',
      noDataInTable: 'No data available',
      header: {
        name: 'Name',
        description: 'Description',
        mandatory: 'Mandatory',
      },
      title: 'Consents',
    },
    consentEdit: {
      consent: {
        consentNameLabel: 'Internal name',
        consentNamePlaceholder: 'Enter internal name',
        consentDescriptionLabel: 'Internal description',
        consentDescriptionPlaceholder: 'Enter internal description',
        consentMandatoryLabel: 'Mandatory',
        consentExternalConsentLabel: 'External consent',
        consentJGIdsLabel: 'Jurisdictional groups',
        consentTypeLabel: 'Consent type',
        consentTypeTriggerText: 'Select',
        consentTypes:
          '{type|{patient:Patient, medical_unit:Clinical site, user: User}}',
        consentJGTrigger: {
          availableItemsText: 'Jurisdictional groups',
          clearText: 'Clear',
          emptyText: 'No jurisdictional groups',
          placeholder: 'Search',
          searchText: 'Search jurisdictional group',
          selectedText: 'Selected jurisdictional groups',
          triggerText: 'Select',
        },
      },
      revision: {
        revisionNameLabel: 'Revision name',
        revisionNamePlaceholder: 'Enter revision name',
        revisionDescriptionLabel: 'Revision description',
        revisionDescriptionPlaceholder: 'Enter revision description',
        revisionLongTextLabel: 'Revision content',
        revisionLongTextPlaceholder: 'Enter revision content ...',
        validityHoursLabel: 'For how long is the consent valid? (in hours)',
        validityHoursPlaceholder:
          "Amount of hours consent is valid after it's been given",
      },
      cannotBeChanged: 'Cannot be changed',
      delete: 'Delete',
      newConsent: 'New',
      onDeleteText: {
        cantDelete:
          'You must unnassign the consent from all jurisdictional groups.',
        deleteError: 'Failed to delete the consent.',
      },
      onSubmitText: {
        success: 'Consent saved successfully',
        createError: 'Failed to create the consent',
        updateError: 'Failed to update the consent',
        revisionError:
          'The consent was saved, but its revision was not. Save again to add it.',
      },
      submit: 'Save',
    },
  },
  copyMedicalRecord: {
    registration: 'Registration of {type:string}',
    skinType: 'Skin type',
    patientWithRash: 'Patient {type:string} presented with a rash',
    patientWithLesion: 'Patient {type:string} presented with a lesion',
    and: 'and',
    dateConnector: '-',
    telediagnosis: 'Telediagnosis',
    differentialDiagnosis: 'Differential diagnosis',
    primaryDiagnosis: 'Diagnosis',
    secondaryDiagnosis: 'Differential diagnosis',
    tertiaryDiagnosis: 'Second differential diagnosis',
    tentativeDiagnosis: 'Tentative diagnosis',
    recommendedAction: 'Recommendation',
    note: 'Additional details',
    evaluatedBy: 'Evaluated and e-signed by',
    by: 'by',
    additionalDetails: 'Additional details',
    plan: 'Plan',
    conditionAge: {
      age: ', that had been present for {age: string}.',
      always: ', that had been present since birth/early life.',
      otherEmpty: ', duration not clearly specified.',
      unknown: ', of unknown duration.',
      none: '.',
    },
    medication: {
      no: 'No recent medications or exposures reported.',
      yes: 'Possible medication/exposure change, details not described further.',
      unknown: 'Unknown if any new medications or exposures.',
      reported: 'Patient mentions {text: string} prior to onset.',
    },
    symptoms: {
      no: 'No associated symptoms.',
      yes: 'Symptoms reported, but not further described.',
      unknown: 'Unclear if any symptoms are present.',
      reported: 'Patient reports {text: string}.',
      otherSymptoms: 'Patient reports {text: string}.',
      ulceration: 'ulceration',
      bleeding: 'bleeding',
      itching: 'itching',
      soreness: 'soreness',
    },
    appearanceChanges: {
      no: 'No changes observed in the lesion.',
      yes: 'Changes in the lesion observed, but not further described,',
      unknown: 'Changes in appearance are unknown.',
      reported: 'Changes in {text: string} observed,',
      otherChanges: 'Patient reports changes: {text: string},',
      age: {
        selected: 'over the {age: string}.',
        other: 'changes have evolved over {age: string}.',
        unknown: 'timeframe for changes is unknown.',
      },
    },
    priorLesionTreatment: {
      no: 'No previous treatment or evaluations.',
      yes: 'Prior interventions reported, but not described further.',
      unknown: 'Unknown if any prior treatment or evaluations.',
      reported: 'Previous treatment/evaluation: {text: string}.',
    },
    priorRashTreatment: {
      no: 'No prior treatments attempted.',
      yes: 'Previous treatment reported, but no specific details are available.',
      unknown: 'Details of previous treatment are unknown.',
      reported: 'Previous treatment with {text: string}.',
    },
    formerSkinCancerAndImmunosuppressantUse: {
      yes_yes:
        'The patient has a history of {cancers: string}, and has also has been treated with {immuno: string}.',
      yes_unspecified:
        'The patient has a history of {cancers: string} and has received immunosuppressant therapy, but these details are not described further.{immuno: string}',
      yes_no:
        'The patient has a history of {cancers: string}, but no history of immunosuppressive therapy.{immuno: string}',
      yes_unknown:
        'The patient has a history of {cancers: string}, but it is unclear whether immunosuppressive therapy has been used.{immuno: string}',
      yes_unanswered:
        'The patient has a history of {cancers: string}.{immuno: string}',

      unspecified_yes:
        'The patient reports a history of skin cancer (details lacking) and has received immunosuppressive therapy, including {immuno: string}.{cancers: string}',
      unspecified_unspecified:
        'The patient has a history of skin cancer and has received immunosuppressive therapy, but further details regarding both are lacking.{cancers: string}{immuno: string}',
      unspecified_no:
        'The patient has a history of skin cancer (details lacking) and has not received immunotherapy.{cancers: string}{immuno: string}',
      unspecified_unknown:
        'The patient reports a history of skin cancer (details lacking), but it is unclear whether immunosuppressive therapy has been used.{cancers: string}{immuno: string}',
      unspecified_unanswered:
        'The patient reports a history of skin cancer, but it is not described further.{cancers: string}{immuno: string}',

      no_yes:
        'No history of skin cancer, but the patient has been treated with {immuno: string}.{cancers: string}',
      no_unspecified:
        'No history of skin cancer, but the patient has received immunosuppressant therapy, but these details are not described further.{cancers: string}{immuno: string}',
      no_no:
        'No history of skin cancer, or immunosuppressive therapy.{cancers: string}{immuno: string}',
      no_unknown:
        'No history of skin cancer, and it is unclear whether immunosuppressive therapy has been used.{cancers: string}{immuno: string}',
      no_unanswered:
        'No history of skin cancer.{cancers: string}{immuno: string}',

      unknown_yes:
        'It is unknown whether the patient has a history of skin cancer, but has been treated with {immuno: string}.{cancers: string}',
      unknown_unspecified:
        'It is unknown whether the patient has a history of skin cancer, but the patient has received immunosuppressant therapy, but these details are not described further.{cancers: string}{immuno: string}',
      unknown_no:
        'It is unknown whether the patient has a history of skin cancer. However the patient has no history of immunosuppressive therapy.{cancers: string}{immuno: string}',
      unknown_unknown:
        'It is unknown whether the patient has a history of skin cancer, and whether immunosuppressive therapy has been used.{cancers: string}{immuno: string}',
      unknown_unanswered:
        'It is unknown whether the patient has a history of skin cancer.{cancers: string}{immuno: string}',

      unanswered_yes:
        'The patient has been treated with {immuno: string}.{cancers: string}',
      unanswered_unspecified:
        'The patient has received immunosuppressant therapy, but these details are not described further.{cancers: string}{immuno: string}',
      unanswered_no:
        'No history of immunosuppressive therapy.{cancers: string}{immuno: string}',
      unanswered_unknown:
        'It is unclear whether immunosuppressive therapy has been used.{cancers: string}{immuno: string}',
    },
    rashDiscomfortAndSystemicSymptoms: {
      yes_yes:
        'The rash causes {discomfort: string} and the patient also experiences {symptoms: string}.',
      yes_unspecified:
        'The rash presents with {discomfort: string}, and the patient also reports systemic symptoms, but further details are not described.{symptoms: string}',
      yes_no:
        'The rash causes {discomfort: string}, with no systemic symptoms present.{symptoms: string}',
      yes_unknown:
        'The rash presents with {discomfort: string}. It is unknown whether the patient is experiencing systemic symptoms.{symptoms: string}',
      yes_unanswered:
        'The rash presents with {discomfort: string}.{symptoms: string}',

      unspecified_yes:
        'The rash presents with unspecified local symptoms and is accompanied by {symptoms: string}.{discomfort: string}',
      unspecified_unspecified:
        'The rash presents with both local and systemic symptoms; however, details regarding these symptoms are lacking.{discomfort: string}{symptoms: string}',
      unspecified_no:
        'The rash exhibits unspecified local symptoms; however, there are no accompanying systemic symptoms.{discomfort: string}{symptoms: string}',
      unspecified_unknown:
        'The rash presents with unspecified local symptoms, but it is unknown whether the patient is experiencing systemic symptoms.{discomfort: string}{symptoms: string}',
      unspecified_unanswered:
        'The rash presents with unspecified local symptoms.{discomfort: string}{symptoms: string}',

      no_yes:
        'The rash is not causing any discomfort. However, the patient experiences {symptoms: string}.{discomfort: string}',
      no_unspecified:
        'While the rash itself is not causing discomfort, the patient is experiencing systemic symptoms, but these have not been specified.{discomfort: string}{symptoms: string}',
      no_no:
        'The rash is not causing any discomfort, and there are no systemic symptoms.{discomfort: string}{symptoms: string}',
      no_unknown:
        'The rash is not causing any discomfort, and it is unknown whether the patient is experiencing systemic symptoms.{discomfort: string}{symptoms: string}',
      no_unanswered:
        'The rash is not causing any discomfort.{discomfort: string}{symptoms: string}',

      unknown_yes:
        'While the patient is experiencing {symptoms: string}, the presence and nature of any concurrent local symptoms are unclear.{discomfort: string}',
      unknown_unspecified:
        'It is unclear if the rash causes discomfort. However, the patient has reported systemic symptoms, but these have not been described further.{discomfort: string}{symptoms: string}',
      unknown_no:
        'It is unclear if the rash causes discomfort, but the patient denies any systemic symptoms.{discomfort: string}{symptoms: string}',
      unknown_unknown:
        'It is unclear if the rash causes any local or systemic symptoms.{discomfort: string}{symptoms: string}',
      unknown_unanswered:
        'It is unclear if the rash causes discomfort.{discomfort: string}{symptoms: string}',

      unanswered_yes:
        'The patient reports {symptoms: string}.{discomfort: string}',
      unanswered_unspecified:
        'The patient has reported systemic symptoms, but these have not been described further.{discomfort: string}{symptoms: string}',
      unanswered_no:
        'There are no systemic symptoms.{discomfort: string}{symptoms: string}',
      unanswered_unknown:
        'It is unclear if the rash causes any systemic symptoms.{discomfort: string}{symptoms: string}',
    },
    rashReliefAndWorseningFactors: {
      yes_yes:
        'The rash improves with {reliefFactor: string}, while it worsens with {worseningFactor: string}.',
      yes_unspecified:
        'The rash improves with {reliefFactor: string}; however, it worsens with factors that have not been described further.{worseningFactor: string}',
      yes_no:
        'The rash improves with {reliefFactor: string}, but no aggravating factors have been identified.{worseningFactor: string}',
      yes_unknown:
        'The rash improves with {reliefFactor: string}, but it is unknown if anything worsens it.{worseningFactor: string}',
      yes_unanswered:
        'The rash improves with {reliefFactor: string}. {worseningFactor: string}',

      unspecified_yes:
        'Despite the patient reporting relief without elaboration, the rash worsens with {worseningFactor: string}.{reliefFactor: string}',
      unspecified_unspecified:
        'The patient reported relief and worsening factors, but details were not provided.{reliefFactor: string} {worseningFactor: string}',
      unspecified_no:
        'It is unclear whether anything provides relief for the rash, but no aggravating factors have been identified.{reliefFactor: string} {worseningFactor: string}',
      unspecified_unknown:
        'Relief was reported without further description, and the presence of worsening factors is uncertain.{reliefFactor: string} {worseningFactor: string}',
      unspecified_unanswered:
        'The patient mentions relief but has not specified further.{reliefFactor: string} {worseningFactor: string}',

      no_yes:
        'No known factors relieve the rash. However, it worsens with {worseningFactor: string}.{reliefFactor: string}',
      no_unspecified:
        'No known factors relieve the rash, however, it worsens with factors that have not been described further.{reliefFactor: string} {worseningFactor: string}',
      no_no:
        'No known factors relieve or aggrevate the rash{reliefFactor: string}{worseningFactor: string}',
      no_unknown:
        'No known factors relieve the rash, and it is unknown if anything worsens it.{reliefFactor: string}{worseningFactor: string}',
      no_unanswered:
        'No known factors relieve the rash.{reliefFactor: string}{worseningFactor: string}',

      unknown_yes:
        'It is unclear whether anything alleviates the rash. However it worsens with {worseningFactor: string}. {reliefFactor: string}',
      unknown_unspecified:
        'It is unclear whether anything alleviates the rash. However, it worsens with factors that have not been described further.{reliefFactor: string}{worseningFactor: string}',
      unknown_no:
        'It is unclear whether anything alleviates the rash, but no aggravating factors have been identified.{reliefFactor: string}{worseningFactor: string}',
      unknown_unknown:
        'It is unclear whether anything alleviates or aggrevates the rash.{reliefFactor: string}{worseningFactor: string}',
      unknown_unanswered:
        'It is unclear whether anything alleviates the rash.{reliefFactor: string}{worseningFactor: string}',

      unanswered_yes:
        'The rash worsens with {worseningFactor: string}.{reliefFactor: string}',
      unanswered_unspecified:
        'The rash worsens with factors that have not been described further.{reliefFactor: string}{worseningFactor: string}',
      unanswered_no:
        'No aggravating factors have been identified.{reliefFactor: string}{worseningFactor: string}',
      unanswered_unknown:
        'It is unknown if anything aggrevates the rash.{reliefFactor: string}{worseningFactor: string}',
    },
  },
  demoPatientDialog: {
    gotIt: 'Got it',
    learnMore: 'Learn more',
    thisIsATest:
      'This is a <>TEST<> patient and the lesions and/or rashes you register will not be evaluated. The date you enter will be deleted after <>24 hours.<>',
    title: 'Dermloop Demo mode',
    youAreEnteringDemoMode:
      'Please note that you are about to enter <>Dermloop Demo mode.<>',
    youAreInDemoMode: 'You are currently in <>Dermloop Demo mode.<>',
  },
  descriptors: {
    accessExpiresOn: 'Access expires on',
    addAdditionalDiagnosis: 'Add additional diagnosis',
    added: 'Added',
    additionalComment: 'Additional comment',
    allCorrectness: 'All',
    allItemsHasBeenSelected: 'All items have been selected',
    anItemHasBeenSelected:
      '{numberOfItemsSelected:number} action{{s}} selected',
    annotate: 'Annotate',
    annotation: 'Annotation',
    annotationRequests: 'Annotation requests',
    annotations: 'Annotations',
    areas: 'Areas',
    assessedBy: 'Assessed by',
    assessmentAction: 'Action',
    assessmentDifficulty: 'Assessment difficulty',
    biopsyTypes: 'Biopsy types',
    classicCriteriaForDiagnosis:
      'Classic criteria for diagnosis {diagnosis:string} ⦁ Select multiple',
    clinic: 'Dermloop Clinic',
    clinicalCase: 'Clinical case',
    clinicalCases: 'Clinical cases',
    clinicalImage: 'Overview image',
    clinicalImageDescription: 'Overview image description',
    clinicalImageEvaluation: 'Overview image evaluation',
    clinicalRole: 'Clinical role',
    clinician: 'Clinician',
    closeUpImage: 'Close-up image',
    closeUpImageDescription: 'Close-up image description',
    closeUpImageEvaluation: 'Close-up image evaluation',
    comment: 'Comment',
    contacted: 'Reviewed',
    contactedBy: 'Reviewed by',
    draftCreatedBy: 'Draft created by',
    correctness: 'Correctness',
    correctnessDefaultTriggerText: 'Correctness: All',
    delete: 'Delete',
    dateOfReferral: 'Date of referral',
    dermloop: 'Dermloop',
    dermoscopicCriteria: 'Dermoscopic criteria',
    dermoscopicCriteriaNr: 'Dermoscopic criterion {index:number}',
    dermoscopicImage: 'Dermoscopic image',
    dermoscopicImageDescription: 'Dermoscopic image description',
    dermoscopicImageEvaluation: 'Dermoscopic image evaluation',
    diagnosedBy: 'Diagnosed by',
    diagnoses: 'Diagnoses',
    diagnosis: 'Diagnosis',
    diagnosisDifficulty: 'Diagnostic difficulty',
    diagnostics: 'Diagnosis',
    diagnosticsDifficulty: 'Diagnostic difficulty',
    differentialDiagnosis: 'Differential diagnosis',
    differentialDiagnosisNr: 'Differential diagnosis {diagnosisNumber:number}',
    difficulty: 'Difficulty',
    disabled: 'Disabled',
    disposition: 'Disposition',
    editLocation: 'Edit location',
    editMedicalRecordLesion: 'Edit lesion #{lesionId:NumberOrString}',
    editMedicalRecordRash: 'Edit rash #{conditionId:NumberOrString}',
    email: 'E-mail',
    enabled: 'Enabled',
    evaluatedBy: 'Evaluated and e-signed by',
    expires: 'Expires',
    finalDiagnosis: 'Final diagnosis',
    formerRatersPlural: 'Former rater{{nrOfRaters:s}}',
    hasFormerSkinCancer: 'Former malignant melanoma or skin cancer',
    histopathologicalDiagnosis: 'Histopathological diagnosis',
    histopathologicalEvaluation: 'Histopathological evaluation',
    history: 'History',
    ifNoDiagnosisRashes:
      'Or select the option below if there is no tentative diagnosis',
    imageNr: '{imageNumber:number}/{totalImages:number} Images',
    imageQuality: 'Image quality',
    imageQualityAssessment: 'Image quality assessment',
    images: 'Images',
    lesionAge: 'Lesion age',
    lesionEditing: 'Lesion editing',
    lesionImagePathology: 'Histopathological assessment',
    lesionImageTeledermatology: 'Teledermatological assessment',
    lesionLocation: 'Lesion location',
    lesionsPlural: 'Lesion{{nrOfLesions:s}}',
    location: 'Location',
    medicalUnit: 'Site',
    medicalUnitInformation: 'Site information',
    medicalUnitRole: 'Site role',
    myCases: 'My cases',
    myRecentRegisteredLesions: 'My recent consults',
    name: 'Name',
    newInvitation: 'New invitation',
    noDateSelected: 'No date selected',
    noDiagnosis: 'No diagnosis',
    noFormerRaters: 'No former raters',
    notAssessedYet: 'Not assessed yet',
    notContacted: 'Not Reviewed',
    notOnSelectedRecord: 'You are not on the selected patient anymore',
    occupation: 'Occupation',
    orSearchFromMostUsed: 'Or search most used',
    otherObjectiveFindings: 'Other objective findings',
    overdueFollowUp: 'Overdue follow-up',
    overview: 'Overview',
    overviewImage: 'Overview image',
    overviewImageDescription: 'Overview image description',
    overviewImageEvaluation: 'Overview image evaluation',
    pathology: 'Biopsied lesions',
    pathologyID: 'Specimen ID',
    patientContacted: 'Response reviewed',
    patientLibrary: 'Search patient library',
    personalInformation: 'Personal information',
    previousTreatmentOrTrauma: 'Previous treatment',
    primaryDiagnosis: 'Primary diagnosis',
    rashesPlural: 'Rash{{nrOfRashes:es}}',
    rateClinicalImage: 'Rate overview image',
    rateCloseUpImage: 'Rate close-up image',
    rateDermoscopicImage: 'Rate dermoscopic image',
    rateImageQuality: 'Rate image quality',
    rateOverviewImage: 'Rate overview image',
    raters: 'Raters (n=)',
    recommendations: 'Recommendations',
    recommendedClinicalAction: 'Recommended clinical action',
    referrer: 'Referrer',
    referrersInfo: 'Referring doctor',
    resultsNr: '{resultNumber:number} result{{s}}',
    resultsNrCapped: '{cap:number}+ results',
    resultsPlural: 'Result{{nrOfResults:s}}',
    reviewedBy: 'Assessed by',
    searchForDiagnosis: 'Search for diagnosis',
    secondaryDiagnosis: 'Secondary diagnosis',
    selectTool: 'Select tool',
    showLess: 'Show less',
    showMore: 'Show more',
    skinTypes: 'Skin types',
    specialisation: 'Specialisation',
    specialityAndCondition: 'Speciality and condition',
    status: 'Status',
    stepIndex: 'Step 0{stepIndex:number}',
    tele: 'Teledermatological referrals',
    teledermatologicalDiagnosis: 'Telediagnosis',
    teleDermatologicalDiagnosis: 'Telediagnosis',
    teleDermatologicalEvaluation: 'Teledermatological evaluation',
    teleEvaluation: 'Teledermatological evaluation',
    tentativeClinicalDiagnosis: 'Tentative clinical diagnosis',
    tertiaryDiagnosis: 'Tertiary diagnosis',
    type: 'Type',
    typeACriteria: 'Type a criteria',
    unableToDetermine: 'Unable to determine',
    unitUsers: 'Unit users',
    userName: 'Username',
    usersPersonalInformation: "User's personal information",
    yourPersonalInformation: 'Your personal information',
    moreOptions: 'More options',
    edit: 'Edit',
  },
  deviceLogin: {
    dialog: {
      title: 'Log in to Dermloop on your Dermkit',
      description:
        'Open the Dermloop app on your Dermkit and scan the QR code below to log in.',
    },
    featureBanner: {
      title: 'Log in to the Dermloop app on your Dermkit using the QR code',
      step1BeforeIcon: 'Click the icon',
      step1AfterIcon: 'in the top right of your screen.',
      step2:
        'Open the Dermloop app on your Dermkit and <>tap the green button<> on the login screen:',
      hostedLoginButton: 'Scan and log in',
      step3:
        "Use your Dermkit to scan the QR code on your PC screen, and you'll be logged in automatically.",
    },
  },
  diagnosisToolbar: {
    addDiagnosis: 'Add additional diagnosis',
    dermatologyDiagnosis: 'Diagnosis from dermatologist',
    dermatologyDiagnosisNote: 'Note',
    diagnosisTypeTitle: 'What type of diagnosis would you like to add?',
    differentialDiagnosis: 'Differential diagnosis {count:number}',
    histoPathologicalDiagnosis: 'Histopathological diagnosis',
    histopathologyDiagnosisNote: 'Note (e.g. pathology report)',
    notePlaceholder: 'Write note...',
    secondaryDiagnosis: 'Secondary diagnosis',
    submitButton: 'Save',
    tertiaryDiagnosis: 'Tertiary diagnosis',
    title: 'Add diagnosis',
  },
  overdueFollowUpToolbar: {
    description:
      'This lesion has been marked for follow-up. The selected follow-up time has passed and the lesion is therefore overdue.',
    stepOne:
      'To remove the “overdue” marking, add a new clinical action for this lesion.',
    stepTwo: 'You can do this using',
    editOrTakeAction: '“Edit/Take action”.',
  },
  displayNameYou:
    '{lastName:string}, {firstName:string} {youString|{(you): (you), *:}}',
  dropdownItems: {
    changeMedicalUnit: 'Change site',
    logout: 'Log out',
    profile: 'Profile',
    settings: 'Settings',
    unitUsers: 'Unit users',
    smartPhrases: 'SmartPhrases',
  },
  errorMessages: {
    alreadyInvitedToMedicalUnit: 'The user is already invited to the site',
    duplicateSorId: 'A clinical site with this SOR ID already exists',
    errorOccurred: 'An error occurred. Please try again',
    errorOccurredUpdatingLesion: 'An error occurred while updating lesion',
    errorOccurredWhileLoadingMedicalRecord:
      'Something went wrong while loading the journal. Please try again.',
    errorOccurredWhileSaving: 'An error occurred while saving',
    conditionNotFound:
      'The condition could not be found. It may have been deleted.',
    errorWhileCreatingAssessment: 'An error occured while creating assessment',
    inputCorrectInputPlease: 'Please input a correct email',
    invalidCPR: 'Invalid CPR',
    lesionNotInHealthRecord: 'Lesion does not match patient',
    pleaseFillOutAllFields: 'Please fill out all fields',
    thisFieldIsRequired: 'This field is required',
    uIdNotFound: '{UID|{SSN:SSN,MRN:MRN}} did not match any health records',
    youAreNotAuthorizedInAnyMedicalUnits:
      'Hi {name:string}, you are not authorized in any sites.',
    youAreNotPermittedToAccessThisPatient:
      'You are not permitted to access this patient',
  },
  fieldPlaceholders: {
    cpr: 'Search for CPR',
    cprOrPathologyID: 'Search for CPR or specimen ID',
    cprOrSpecimentID: 'Search for CPR or specimen ID',
    enterCommentHere: 'Enter your comment here...',
    loading: 'Loading...',
    mrn: 'Search for mrn',
    mrnOrName: 'Search for mrn or name',
    cprOrName: 'Search for CPR or name',
    otherClinicalAction: 'Enter other clinical action here...',
    pathologyId: 'Search for specimen ID',
    searchDiagnosis: 'Search for diagnosis',
    searchForCriteria: 'Search for criteria',
    searchLocation: 'Search location',
    selectMedicalUnit: 'Select site',
    selectOccupation: 'Select occupation',
    selectSpecialisation: 'Select specialisation',
    typeHereToEnterDescription: 'Type here to enter description...',
  },
  filters: {
    skinType: 'Type',
    resetFilter: 'Reset filter',
    jurisdictionalGroup: {
      title: 'Network',
      searchText: 'Search for network',
      searchPlaceholder: 'Enter network',
      listTitle: 'Networks',
      selectedListTitle: 'Selected networks',
      noResultsText: 'No networks found',
    },
    medicalUnit: {
      title: 'Sites',
      searchText: 'Search for sites',
      searchPlaceholder: 'Enter site name',
      listTitle: 'Sites',
      selectedListTitle: 'Selected sites',
      noResultsText: 'No sites found',
    },
    dateOfReferral: {
      title: 'Date of referral',
    },
    diagnoses: {
      availableDiagnosesText: 'All diagnoses',
      clearText: 'Clear diagnoses',
      diagnosisSearchText: 'Search for diagnosis',
      emptyText: 'No diagnoses found',
      placeholder: 'Enter diagnosis',
      selectedDiagnosisText: 'Selected diagnoses',
      title: 'Diagnosis',
      withTabs: {
        clinicalTabText: 'Clinical diagnosis',
        finalTabText: 'Final diagnosis',
        histopathologicalTabText: 'Histopathological diagnosis',
        teleTabText: 'Telediagnosis',
        tentativeTabText: 'Tentative diagnosis',
      },
    },
    actions: {
      title: 'Recommended actions',
      clearText: 'Clear actions',
      availableActionsText: 'All actions',
      selectedActionsText: 'Selected actions',
      searchText: 'Search for actions',
      placeholder: 'Action name',
    },
    difficulty: {
      title: 'Difficulty',
    },
    location: {
      availableLocationText: 'All locations',
      clearText: 'Clear locations',
      emptyText: 'No locations found',
      locationSearchText: 'Search for location',
      placeholder: 'Enter location',
      selectedLocationText: 'Selected locations',
      title: 'Location',
    },
    referralCreatedAfter: {
      title: 'eConsult created after',
    },
    referralCreatedBefore: {
      title: 'eConsult created before',
    },
    search: {
      insuranceInfo: 'Search by patient name or account number',
    },
    status: {
      all: 'All',
      annotated: 'Annotated',
      approved: 'Approved',
      assessed: 'Assessed',
      canceled: 'Canceled',
      closed: 'Closed',
      newReferral: 'New eConsult',
      pendingInformation: 'Pending information',
      pending: 'Pending',
      released: 'Released',
      skipped: 'Skipped',
      title: 'Status',
    },
    marked: {
      title: 'Bookmark',
      all: 'All eConsults',
      marked: 'Marked eConsults',
    },
    multiSelect: {
      availableStatus: 'Available',
      clearText: 'Reset',
      selectedStatus: 'Selected',
      triggerText: 'Status',
    },
  },
  genders: {
    female: 'female',
    male: 'male',
  },
  recentRegistrations: {
    title: 'My registrations',
    empty: 'No recent registrations',
  },
  headerElements: {
    annotations: 'Annotations',
    biopsies: 'Biopsies',
    cases: 'Cases',
    home: 'Home',
    announcementBanner: {
      dismiss: 'Dismiss',
      minimise: 'Minimise',
      expandCritical: '1 critical announcement — expand',
    },
    demoModeHeader: {
      dermloopDemoMode: 'Dermloop Demo mode',
    },
    statistics: 'Statistics',
    teleReferrals: 'eConsults',
  },
  informationFieldLabels: {
    email: 'Email',
    firstName: 'First name',
    lastName: 'Last name',
    phoneNumber: 'Phone number',
  },
  administrationReferralsTable: {
    title: 'Unanswered eConsults',
    noEConsults: 'No unanswered eConsults in this federation',
    columns: {
      eConsultNumber: 'eConsult #',
      created: 'Created',
      medicalUnit: 'Medical unit',
      type: 'Type',
      draft: 'Draft',
      released: 'Released',
      releasedDate: 'Released date',
      releasedToAllowed: 'Released to allowed members',
      status: 'Status',
      assignment: 'Assignment',
    },
    values: {
      tele: 'Tele',
      physical: 'Physical',
      unassigned: 'Unassigned',
    },
    statuses: {
      open: 'Open',
      pendingInformation: 'Pending information',
      closed: 'Closed',
      cancelled: 'Cancelled',
    },
    filters: {
      available: 'Available',
      selected: 'Selected',
      newestFirst: 'Newest first',
      oldestFirst: 'Oldest first',
      sortCreated: 'Sort by created',
    },
    actions: {
      moreOptions: 'More options',
      viewCase: 'View case',
      changeStatus: 'Change status',
      release: 'Release',
    },
    toast: {
      statusUpdated: 'Status updated',
      statusUpdateFailed: 'Could not update the status',
      released: 'eConsult released',
      releaseFailed: 'Could not release the eConsult',
    },
  },
  insuranceInfoTable: {
    accNr: 'Acc. No.',
    assessed: 'Assessed',
    assign: 'Assign',
    assignEConsultTo: 'Assign eConsult to...',
    assignTo: 'Assign to...',
    cancelEConsult: 'Cancel eConsult',
    cancelled: 'Canceled',
    closed: 'Closed',
    pendingInformation: 'Pending information',
    closeEConsult: 'Close eConsult',
    dob: 'DOB',
    eConsultId: 'eConsult ID',
    editInsuranceInfoToolbar: {
      assignedTo: 'Assigned to',
      changeStatus: 'Change status',
      dateFormat: 'Date format',
      dob: 'DOB',
      downloadPDF: 'Download PDF',
      resendFax: 'Resend Fax',
      firstName: 'First name',
      icd10Code: 'ICD10 Code',
      insuranceNumber: 'Insurance number',
      insurer: 'Insurer',
      lastName: 'Last name',
      mrn: 'Account number',
      patientAddress: 'Patient address',
      patientCity: 'Patient city',
      patientState: 'Patient state',
      patientZip: 'Patient zip',
      payer: 'Payer',
      placeholders: {
        dob: 'The patients birthday',
        firstName: 'The patient first name',
        icd10Code: 'ICD10 Code',
        insuranceNumber: "The patient's insurance number",
        insurer: 'Write the name of the insurance company',
        lastName: 'The patient last name',
        mrn: 'The patients account number',
        patientAddress: 'The patients address',
        patientCity: 'Which city are the patient from?',
        patientState: 'The patients state',
        patientZip: 'Zip',
        payer: 'Who is paying?',
        question: 'Why does the patient need an eConsult?',
      },
      question: 'Clinical question',
      save: 'Save',
      saveAndRelease: 'Save and release',
      title: 'Edit insurance info',
      validationMessages: {
        icd10Code: 'ICD10 Code is required',
        mrn: 'Account number is required',
        name: 'Name is required',
        patientAddress: 'Address is required',
        patientCity: 'City is required',
        patientState: 'State is required',
        patientZip: 'Zip code is required',
        payer: 'Payer is required',
        question: 'Clinical question is required',
      },
    },
    superSeededText: 'eConsult superseeded',
    superSeededTooltip:
      'eConsult superseeded by a submission with a new action. Please review the new submission.',
    exportCSV: 'Export CSV',
    from: 'From',
    moreOptions: 'More options',
    network: 'Network',
    newReferral: 'New',
    noEConsults: 'No eConsults were found',
    patient: 'Patient',
    referral: 'eConsult',
    released: 'Released',
    searchSpecialistPlaceholder: 'Search for specialist',
    specialist: 'Specialist',
    status: 'Status',
    suggestedSpecialist: 'Suggested specialist',
    title: 'eConsults',
    toast: {
      errorSaving: 'Error while saving insurance info',
      errorCanceled: 'Error while canceling the eConsult',
      errorClosing: 'Error while closing the eConsult',
      errorExportCSV: 'Could not download CSV',
      errorOnReassignSpecialist:
        'Something went wrong trying to reassign the referral to another specialist',
      errorUnCanceling: 'Error while un-canceling the eConsult',
      successfullyCanceled: 'eConsult was successfully canceled',
      successfullyMarkedPendingInformation:
        'eConsult was marked as pending information',
      errorMarkingPendingInformation:
        'Error while marking the eConsult as pending information',
      successfullyClosed: 'eConsult was successfully closed',
      successfullyExportedCSV: 'CSV downloaded',
      successfullyReassignedSpecialist:
        'Successfully reassigned referral to another specialist',
      successfullyUnCanceled: 'eConsult was successfully un-canceled',
      successfullySavedInsuranceInfo: 'Insurance info was successfully saved',
    },
    unCancelEConsult: 'Uncancel eConsult',
    viewCaseDetails: 'View case details',
  },
  invitationExpired: {
    title: 'Dermloop Desktop',
    subtitle: 'Invitation expired',
    messageWithEmail: 'The invitation to (<>{email}<>) has expired.',
    messageWithoutEmail: 'The invitation has expired.',
    contactSuperuser:
      'Please contact your superuser if you need access to Dermloop.',
  },
  macroscopicInspectionType:
    '{type|{None: None, Assymetrical: Assymetrical, Irregular Border: Irregular Border, Several Colours: Several Colours, Diameter >6 mm: Diameter >6mm, Other: Other}}',
  medicalRecordValues: {
    age: '(time: {age:string})',
    appearanceChanges: '{triageInfo:string}',
    none: 'None',
    notRegistered: 'Not registered',
  },
  clinicalAssessmentToolbar: {
    missingImagesTitle: 'Missing images!',
    missingImagesDescription:
      'The necessary images do not exist on this case. Please capture them from the mobile app.',
  },
  pathologyAssessmentToolbar: {
    createAssessmentSuccess:
      'You successfully created the pathology assessment',
    primaryDiagnosisErrorMessage: 'It is required to pick a primary diagnosis',
    diagnosisDifficultyErrorMessage: 'It is required to select a difficulty',
    breslowThicknessErrorMessage:
      'Insert a valid number with maximum 1 decimal',
    breslowThicknessPlaceholder: 'Type breslow thickness here...',
    breslowThicknessTitle: 'Breslow thickness (mm)',
    breslowThicknessUnknown: 'Tumor thickness unknown (Tx)',
    referredBy: 'eConsult sent by',
  },
  patientPage: {
    markPatient: {
      successfullyBookmarkedCondition: 'eConsult marked',
      successfullyRemovedBookmark: 'eConsult unmarked',
      mark: 'Mark',
      marked: 'Marked',
    },
    alert: {
      cancel: 'Cancel',
      pleaseNoteNotContactedPatient:
        'Please note that the patient has currently NOT been marked as contacted.',
      takeAction: 'Take Action',
      takeNewClinicalAction: 'Take new clinical action?',
      weAssumePatientIsContacted:
        'When you choose a new clinical action, we assume that you have contacted the patient first.',
    },
    // Labels for the mobile floating button that jumps to the assessment panel.
    // Picked from the active toolbar state (see patient-page.tsx).
    jumpToToolbar: {
      completeDraft: 'Complete draft',
      saveAssessment: 'Save assessment',
      addDiagnosis: 'Add diagnosis',
      completeEConsult: 'Complete e-consult',
      completePathology: 'Complete pathology',
      reviewEConsult: 'Review e-consult',
      reviewPathology: 'Review pathology',
      completeFollowUp: 'Complete follow-up',
      clinicalCase: 'Clinical case',
    },
    editContact: {
      showAll: 'Show phone number, email and address',
      hideAll: 'Hide phone number, email and address',
      showPhone: 'Show phone number',
      hidePhone: 'Hide phone number',
      showAddress: 'Show email and address',
      hideAddress: 'Hide email and address',
      missingInfo: 'Missing contact info',
      addPhone: 'Click here to add phone number',
      addEmail: 'Click here to add email',
      addAddress: 'Click here to add address',
      editPhone: 'Edit phone number',
      editEmail: 'Edit email',
      editAddress: 'Edit address',
      phoneNumber: 'Phone number',
      address: 'Address',
      city: 'City',
      postalCode: 'Postal code',
      email: 'Email',
      invalidEmail: 'Enter a valid email address',
      invalidPostalCode: 'Enter a valid postal code',
      confirm: 'Confirm',
      discard: 'Discard',
      toastSuccess: 'Contact info updated',
      toastError: 'Could not update contact info',
    },
  },
  plaintext: {
    additionalMedicalUnitRoles: 'Additional site role{{nrOfRoles:s}}',
    all: 'All',
    contact: 'Contact',
    copiedFromDermloop: 'Copied from Dermloop',
    inviteByEmail: 'Invite by email',
    linkToCondition: 'Link to condition',
    melatechsTeam: "Melatech's team",
    on: 'on',
    onceYouRemove: 'Once you remove',
    restoreAccessByInvitingAgain:
      'You can restore their access by inviting the user again.',
    roleToThisUser: 'roles to this user.',
    theyWillNoLongerHaveAccessTo: 'they will no longer have access to',
    thisInvitationExpires: 'This invitation expires {date:string}',
    thisMedicalUnit: 'this site.',
    toAddAdditional: 'to add additional',
    toRemoveThisUser: 'to remove this user',
    useDermloopMobileApp: 'Use Dermloop Capture to register new images',
    yearOld: 'year old',
    youHaveBeenInvitedTo: 'You have been invited to',
  },
  referralStatisticsPage: {
    dailyStatisticsMissing: 'No daily statistics data available',
    dailyStatisticsTitle: 'Daily statistics',
    dailyStatisticsToDateLabel: 'Two weeks before...',
    jurisdictionalGroupNameColumn: 'Jurisdictional group',
    pending24HoursColumn: '24-48 hours',
    pending48HoursColumn: '48-96 hours',
    pending96HoursColumn: '>96 hours',
    title: 'Referral statistics',
    totalPendingColumn: 'Total pending',
    totalPendingReferrals24HoursTitle: '24-48 hours',
    totalPendingReferrals24HoursTooltip:
      'Referrals that are older than 24 hours and younger than 48 hours',
    totalPendingReferrals48HoursTitle: '48-96 hours',
    totalPendingReferrals48HoursTooltip:
      'Referrals that are older than 48 hours and younger than 96 hours',
    totalPendingReferrals96HoursTitle: '>96 hours',
    totalPendingReferrals96HoursTooltip:
      'Referrals that are older than 96 hours',
    totalPendingReferralsTitle: 'Total pending referrals',
    totalPendingReferralsTooltip: 'Total number of pending referrals',
    valueCouldNotBeLoaded: 'Could not be loaded',
  },

  seranoteButton: {
    new: 'New',
  },
  searchResults: {
    adjustMessage:
      "Try adjusting your search or filter to find what you're looking for",
    noResults: 'No results found',
  },
  smartPhrases: {
    diagnosisNotFound: 'Diagnosis not found...',
    actionNotFound: 'Action not found...',
    teleDialog: {
      updateHeader: 'Update existing SmartPhrase?',
      updateContent: 'This will replace the existing SmartPhrase for:',
      diagnosisAndAction: '{diagnosis:string} and {recommendedAction:string}.',
      currentSmartPhrase: 'Current SmartPhrase:',
      cancelButton: 'Cancel',
      updateButton: 'Update',
    },
    changeAlert: {
      title: 'Replace current text with SmartPhrase?',
      content:
        "You've made changes to the current smartphrase. Do you want to keep your current text, or replace it with the smartphrase for the new selection?",
      keepButton: 'Keep my current text',
      replaceButton: 'Replace with SmartPhrase',
    },
    errorMessages: {
      phraseRequired: 'SmartPhrase is required',
      actionRequired: 'Recommended action is required',
      diagnosisRequired: 'Diagnosis is required',
    },
    toast: {
      successfullyUpdatedSmartPhraseOption:
        'Successfully {state|{enabled:enabled, disabled:disabled}} your SmartPhrases',
      errorUpdatingSmartPhrasesOption:
        'Something went wrong, we could not update your SmartPhrases options',
      successfullyDeleted: 'Successfully deleted the SmartPhrase',
      success: 'SmartPhrase created successfully.',
      updated: 'SmartPhrases successfully updated',
      error: 'Error creating SmartPhrase.',
      errorDeletion: 'Error while trying to delete SmartPhrase',
      noSmartPhraseId:
        'Error updating SmartPhrase. No smart phrase ID was found.',
    },
    smartPhrases: 'SmartPhrases',
    results: 'result{{nrOfImages:s}}',
    subTitle:
      'These smart phrases will appear automatically in the “Comment” field when you have selected a diagnosis and an action for a case.',
    toolbar: {
      diagnosisPickHeader: 'Choose diagnosis and recommended action',
      diagnosisAndActionHeader: 'Diagnosis and recommended action',
      diagnosisStep: 'Diagnosis',
      enterDiagnosis: 'Enter diagnosis',
      enterSmartPhrase: 'Enter your SmartPhrase here...',
      addSmartPhrase: 'Add SmartPhrase',
      recommendedAction: 'Recommended action',
      editSmartPhrase: 'Edit SmartPhrase',
      saveChanges: 'Save Changes',
      insertSmartPhrase: 'Insert SmartPhrase',
      clearNote: 'Clear note',
      updateSmartPhrase: 'Update SmartPhrase',
      smartPhraseSaved: 'SmartPhrase Saved',
      createSmartPhrase: 'Create SmartPhrase',
      enterNoteHere: 'Enter your note here...',
      smartPhrase: 'SmartPhrase',
    },
    table: {
      delete: 'Delete',
      cancel: 'Cancel',
      deleteSmartPhrase: 'Delete SmartPhrase?',
      cantBeUndone: 'This action cannot be undone.',
      diagnosis: 'Diagnosis',
      recommendedAction: 'Recommended action',
      smartPhrase: 'SmartPhrase',
      noSmartPhrases: "You don't have any SmartPhrases yet.",
      createSmartPhrase: 'Create SmartPhrase',
      searchPlaceholder: 'Search for SmartPhrase',
      smartPhrasesEnabled: 'SmartPhrases enabled',
    },
    tooltips: {
      addSmartPhrase:
        'To create a SmartPhrase, you need to select both a diagnosis and a recommendation.',
      actionAlreadyExists:
        'SmartPhrase already exists for the selected combination.',
    },
  },
  conditionHistory: {
    actions: {
      addedNewDiagnosis: 'New diagnosis',
    },
    title: {
      newDiagnosis:
        'Added new {diagnosisType|{pathologicalDiagnosis:histopathological, dermatologicalDiagnosis:dermatological, *:}} diagnosis',
      pathology: 'Histopathological evaluation',
      patientContacted: 'Response reviewed',
      conditionDetails: '{type:string} info changed',
      teledermatology: 'Teledermatological evaluation',
    },
  },
  conditionList: {
    testPatient: 'Test patient',
  },
  conditionOverview: {
    addNewDiagnosis: 'Add new diagnosis',
    sendToJournal: 'Send to journal',
    sendToJournalSuccess: 'Journal sent',
    sendToJournalError: 'Error sending to journal. Try again',
  },
  conditionPage: {
    backToPatientOverview: 'Back to patient overview page',
    collapseSidebar: 'Collapse sidebar',
    patientSectionLabel: 'Patient',
    specialitySectionLabel: 'Speciality',
    locationSectionLabel: 'Location',
    draftBadge: 'Draft',
    editCase: 'Edit case',
    copyJournal: 'Copy journal',
    more: 'More',
    downloadAllPhotos: 'Download all photos',
    downloadingPhotos: 'Downloading photos...',
    imagesPlaceholder: 'Images',
    medicalJournal: 'Medical journal',
    assessment: 'Assessment',
    taskCompleted: 'Task completed',
    taskCompletedNext: 'Continue to the next task.',
  },
  conditionResponse: {
    title: 'Specialist answer',
    sent: 'Answer sent',
    sentToast: 'Your answer was sent to the referring doctor',
    couldNotOpen: 'The answer form could not be opened. Please try again.',
    noAssignedForm:
      'No answer form is set up for this condition type, so this referral cannot be answered here. Please contact support.',
  },
  conditionResponseReview: {
    title: 'Review the answer',
    send: 'Mark as reviewed',
    sentToast: 'Case marked as reviewed',
    reviewedBy: 'Reviewed by {name:string}',
    ratingTooltip:
      'This rating is collected to ensure that specialist answers are consistently of a high standard. It will not be visible to the specialist.',
  },
  moveConditionDialog: {
    title: 'Move condition',
    description:
      'Move this condition and everything attached to it to another patient in the same jurisdictional group. The destination patient keeps their own identity.',
    whatMoves: 'What moves',
    conditionLabel: 'Condition',
    currentPatient: 'Current patient',
    destinationPatient: 'Destination patient',
    unnamedPatient: 'Unnamed patient',
    triages: 'Triages ({count:number})',
    detailsNoteTitle: 'Moves along with the condition',
    detailsNote:
      'Condition details, diagnoses, markers and the images attached to these triages follow the condition automatically.',
    createNewPatient: 'Create new patient',
    createPatientTitle: 'Create the destination patient',
    createPatientFromLookup:
      'The details come from the national patient register and are saved as shown.',
    registerNotFound:
      'The national patient register has no person with this identifier, so the case cannot be moved to it.',
    registerDead:
      'The national patient register lists this person as deceased, so the case cannot be moved to them.',
    registerFailed:
      'The national patient register could not be reached. Go back and try again.',
    createPatientManually:
      "No register lookup is available here, so enter the destination patient's details.",
    lookingUp: 'Looking the identifier up...',
    selectGender: 'Select gender',
    createPatientNoteTitle: 'Only the identity is entered here',
    createPatientNote:
      'Name, identifier, birth date and gender. Contact details and consents are not moved.',
    findPatient: 'Find the destination patient',
    search: 'Search',
    noPatientFound:
      'No patient with this identifier exists in this jurisdictional group.',
    samePatient: 'The condition already belongs to this patient.',
    searchFailed: 'The search failed. Please try again.',
    fields: {
      firstName: 'First name',
      lastName: 'Last name',
      birthDate: 'Date of birth',
      gender: 'Gender',
    },
    confirmTitle: 'Confirm move',
    confirmDescription:
      'The condition, its triages, details and images will belong to the destination patient. This cannot be undone from the app.',
    confirmRenameDescription:
      "This is the current patient's only case, so no new patient is created. The current patient gets the identity shown here, and their contact details and consents are removed. This cannot be undone from the app.",
    sourceDeletedTitle: 'The current patient will be deleted',
    sourceDeletedNote:
      'They have no other cases. Their name, identifier, contact details and consents are removed.',
    next: 'Next',
    back: 'Back',
    cancel: 'Cancel',
    move: 'Move condition',
    moving: 'Moving...',
    toastMessages: {
      moveSuccess: 'Condition moved',
      moveFailed: 'Failed to move condition: {error:string}',
    },
  },
  conditionForm: {
    notAnswered: 'Not answered',
    mostUsedDiagnoses: 'Most used',
    noSubmission: 'No form submission for this condition',
    expandImage: 'Open {label} in full screen',
    noPhotoCaptured: 'No {label} photo was captured',
    previousImage: 'Previous image',
    nextImage: 'Next image',
    nImages: '{count:number} image{{count:s}}',
    previousThumbnails: 'Previous thumbnails',
    nextThumbnails: 'Next thumbnails',
    closeImage: 'Close image',
    downloadImage: 'Download image',
    showFlashImage: 'Show with flash',
    hideFlashImage: 'Show without flash',
    showNoFlashImage: 'Show without flash',
    hideNoFlashImage: 'Show with flash',
    showImageMarker: 'Show marker',
    hideImageMarker: 'Hide marker',
    compare: 'Compare',
    compareImages: 'Compare images',
    imageActionUnavailable: 'Not available yet',
    unsupportedWidget:
      'This form contains a question this version cannot display.',
    unsupportedLocationMap:
      'This version cannot display the anatomy view this form uses.',
    notePlaceholder: 'Add more details here...',
    placeMarkerHint: 'Click the illustration to set the marker',
    editLocation: 'Edit location',
    saving: 'Saving...',
    savedAt: 'Saved {time:string}',
    saveFailed: 'Changes not saved',
    completeRegistration: 'Complete registration',
    conclude: 'Conclude',
    discardDraft: 'Discard draft',
    submissionIncomplete: 'Some required questions are still unanswered.',
    imagesNeedCapture:
      'A required photo is missing. Photos can only be added in Capture.',
    staleRevision:
      'Someone else saved this case after you started editing. Reload to edit the latest version.',
    reload: 'Reload',
    changesSaved: 'Changes saved',
    awaitingAnswer: 'Awaiting answer',
    answered: 'Answered',
    teleReferral: 'Tele-referral',
    saveChanges: 'Save changes',
    saveAndSendTeleReferral: 'Save and send tele-referral',
    saveAndRecordActions: 'Save and record {count:number} action{{count:s}}',
    actionIncomplete: 'Finish the details of each new action, or remove it.',
    clinicalActions: 'Clinical actions',
    cancelled: 'Cancelled',
    closed: 'Closed',
    recorded: 'Recorded',
    cancelReferral: 'Cancel referral',
    keepReferral: 'Keep referral',
    newClinicalAction: 'New clinical action',
    noNewAction: 'No new action',
    anotherAction: 'Another {action:string}',
    actionLimitTeleReferral:
      'A tele-referral is already awaiting an answer. The specialist will see these changes.',
    actionLimitDismissal: 'The latest action already concludes the case.',
    actionLimitMixed: 'A save records one type of action.',
    cancelReferralTitle: 'Cancel this tele-referral?',
    cancelReferralDescription:
      'The specialist will no longer see it. It is cancelled when you save, and you can undo it until then.',
    undo: 'Undo',
    removeExistingAction: 'Remove {label:string}',
    dropQueuedAction: 'Drop {label:string}',
    saveAndCancelTeleReferral: 'Save and cancel tele-referral',
    saveAndRemoveActions: 'Save and remove {count:number} action{{count:s}}',
    saveAndApplyChanges: 'Save and apply {count:number} change{{count:s}}',
    actionRequired: 'Add an action to complete the registration.',
    encounterRequired: 'Open this case from Epic to complete the registration.',
    actionsNotAllowed:
      'These actions cannot be recorded together. Undo one of the changes.',
    actionSent: 'Sent {date:string}',
    actionSentBy: 'Sent {date:string} by {name:string}',
    actionTakenBy: '{date:string} by {name:string}',
    removedOnSave: 'Removed when you save',
    cancelledOnSave: 'Cancelled when you save',
  },
  patientDeviceLogin: {
    open: 'Open in Dermkit',
    close: 'Close QR code',
    instruction: 'Open Dermkit on your phone and scan the code.',
    autoExpiry: 'The code expires automatically after scanning.',
    remaining: '{seconds:number} seconds remaining',
    loading: 'Creating QR code',
    error: 'The QR code could not be created.',
    retry: 'Try again',
  },
  tableColumns: {
    latestActivityOn: 'Latest activity:',
    activityBy: 'by',
    referredOn: 'Referred on:',
    tentativeShort: 'T:',
    finalShort: 'F:',
    referred: 'Referred',
    on: 'On:',
    by: 'By:',
    latestActionAndStatus: 'Latest action & status',
    clinical: 'Clinical:',
    clinicalShort: 'C:',
    tele: 'Tele:',
    teleShort: 'T:',
    histology: 'Histology:',
    histologyShort: 'H:',
    internalClinician: 'Internal clinician',
    externalClinician: 'External clinician',
    released: 'Released',
    unknownDate: 'Unknown date',
  },
  patientInfoPage: {
    backToCases: 'Back to cases',
    chooseSpecialityTitle: 'Choose a speciality to continue',
    chooseSpecialitySubtitle:
      'Opens the condition view with that speciality pre-selected.',
    registrationUnit: '{{count:registration|registrations}}',
    noRegistrationsYetTile: 'No registrations yet',
    noSpecialitiesTitle: 'No specialities registered yet',
    noSpecialitiesSubtitle:
      'This patient has no active speciality registrations. Browse recent registrations below.',
    specialitiesUnavailableTitle: 'Specialities could not be loaded',
    specialitiesUnavailableSubtitle:
      'Reload the page to try again, or open a recent registration below.',
    orOpenRecentRegistration: 'or open a recent registration',
    columnRegistration: 'Registration',
    columnDiagnoses: 'Diagnoses',
    columnActionAndStatus: 'Action & status',
    columnLatestActivity: 'Latest activity',
    tentative: 'Tentative:',
    final: 'Final:',
    awaitingEvaluation: 'Awaiting evaluation',
    noRegistrations: 'No registrations yet',
    registrationsUnavailable:
      'Registrations could not be loaded. Reload the page to try again.',
    openTasksLabel: 'Open tasks',
    openTasksCount: 'Open tasks: {count:number}',
    noOpenTasks: 'No open tasks',
    openTasksLoading: 'Loading open tasks…',
    openTasksUnavailable: 'Open tasks could not be loaded',
    taskEconsultsToReview: 'eConsults to review',
    taskDraftRegistrations: 'Draft registration',
    taskOverdueFollowUps: 'Overdue follow-up',
  },
  statuses: {
    allStatuses: 'All statuses',
    annotated: 'Annotated',
    approved: 'Approved',
    correct: 'Correct',
    incorrect: 'Incorrect',
    noFormerAnnotations: 'No former annotations',
    pending: 'Pending',
    overdue: 'Overdue',
    pendingInvitation: 'Invitation pending',
    unspecified: 'Unspecified',
  },
  statusMessages: {
    anotherLesionBeingEdited: 'Another lesion is being edited',
    biopsySuccessfullyClosed: 'Biopsy has been registered',
    closeMenuToActivateButton:
      'To activate this button, close the menu to the right',
    copiedToClipboard: 'Copied to clipboard',
    diagnosisNotFound: 'Diagnosis not found',
    invitationAccepted: 'Invitation accepted',
    invitationRejected: 'Invitation rejected',
    invitationSentToEmail: 'Invitation has been sent to {email:string}',
    lesionEditingDoesNotLiveHere: 'Lesion editing does not live here anymore.',
    lesionNotPartOfAnyTask: 'This lesion is not part of any task',
    lesionNotRegisteredForHistopathologicalEvaluation:
      'This lesion is not registered for histopathological evaluation',
    lesionNotRegisteredForTeledermatologicalEvaluation:
      'This lesion is not registered for teledermatological evaluation',
    mustBeValidPhoneNumber: 'Phone number does not match country code',
    noClassicCriteriaAvailableForDiagnosis:
      'No classic criteria available for diagnosis',
    noClassicCriteriaSelected: 'No classic criteria selected',
    patientHasBeenContacted: 'Patient has been contacted',
    pleaseEnterPhoneNumber: 'Please enter your phone number',
    pleaseSelectADeparment: 'Please select a department to continue',
    profileUpdated: 'Profile updated',
    required: 'Required',
    selectLesionFromSidebar: 'Please select a lesion from left sidebar',
    somethingWentWrong: 'Something went wrong.',
    successfullySaved: 'Saved',
    teleReferralRequestSent: 'eConsult has been sent',
    teleReferralSuccessfullyClosed: 'eConsult has been sent',
    triageSuccessfullyClosed: 'Consult completed',
    undiagnosableDueToImageQuality:
      'Diagnosis not possible due to image quality',
    userHas14DaysToAccept: 'User will have 14 days to accept your invitation',
    userRemovedSuccessfully: 'User removed successfully',
    userUpdated: 'User updated',
    youAreAboutToLeave: 'You are about to leave',
  },
  teleAnswerRating: {
    title: 'How was this evaluation?',
    good: 'Good',
    bad: 'Bad',
    tooltip:
      'This rating is collected to ensure that the dermatological evaluations are consistently of a high standard. This rating will not be visible to the teledermotologist.',
    goodPlaceholder: 'What you liked about the evaluation...',
    badPlaceholder: 'Which topics could be improved in the evaluation...',
    successfullyEvaluatedCase: 'Successfully evaluated the case',
  },
  tasks: {
    tabOne: 'Own tasks',
    tabTwo: 'Site tasks',
    action: 'Action',
    noTasksHere: 'You have finished your task with success.',
    continueToNextTask: 'Continue to next tasks, if there is any left.',
    annotationFeedback: {
      description: 'Annotations that have been reviewed and received feedback',
      title: 'Annotation feedback',
    },
    annotationsThatNeedReviews: {
      description: 'Annotations which have not yet been reviewed',
      title: 'Review annotations',
    },
    drafts: {
      description: 'Drafts that have not yet been completed',
      title: 'Drafts',
    },
    biopsies: {
      description: 'New biopsy results',
      title: 'Biopsies',
    },
    buttons: {
      start: 'Start',
      viewAll: 'View all',
    },
    menuItem: 'Tasks',
    overdueFollowUps: {
      description: 'Conditions registered for follow-up that need to be seen',
      title: 'Overdue follow-ups',
    },
    pendingAnnotations: {
      description: 'Training lesions which have not yet been annotated',
      title: 'Pending annotations',
    },
    teleAssessments: {
      description: 'New teledermatological assessments',
      title: 'Teleassessments',
    },
    title: 'Tasks',
    total: 'Total',
  },
  teledermatologicalAssessmentToolbar: {
    claimReferralButton: 'Claim eConsult',
    casesSkipped:
      'Skipped {count:number} unavailable {{eConsult|eConsults}} in your list',
    errorOccuredWhileClaimingReferral:
      'An error occurred while claiming the eConsult. Please try again.',
    referralClaimedByOtherUserDescription:
      'Another specialist is already answering this eConsult.',
    referralNotAnswerableDescription:
      'You do not have access to answer this eConsult.',
    referralNotClaimedTitle: 'eConsult not claimed yet',
    referredBy: 'eConsult sent by',
    videoLink: 'Click here to watch a video on how to take good images',
    videoDialogTitle: 'Image improvement videos',
    dermoscopicImageTitle: 'Dermoscopic image',
    overviewImageTitle: 'Overview image',
    commentTitle: 'Specialist response',
    commentPlaceholder: 'Enter your comment here...',
    informReferrerAboutAcuteDiagnosis:
      'Inform the referrer about acute diagnosis',
    tooltips: {
      imageQualityRatingTitle:
        'Used for educating the referring physician and improving Dermloop image quality.',
      teleDiagnosisTitle:
        'All selected diagnoses are saved in Dermloop and may be used for statistics.',
      recommendationsTitle:
        'Recommended clinical action for the referring physician.',
      commentTitle:
        'Recommendations for the referring physician. Educational text, recommended treatment, elaboration of recommendation, etc.',
    },
    timeSpentReviewing: 'Time spent reviewing',
    timeSpentReviewingDescription:
      "A minimum of five minutes reviewing the patient's record and preparing your clinical response is required to bill for CPT code 99451.",
    timeSpentReviewingPlaceholder: 'Time spent reviewing in minutes',
  },
  teleReferralTable: {
    columns: {
      eConsultIdNumber: 'eConsult ID',
      status: 'Status',
      clinicalDiagnosis: 'Clinical diagnosis',
      teleDermatologicalDiagosis: 'Tele diagnosis',
      referrer: 'Referrer',
      dateOfReferral: 'Referral date',
      assessedBy: 'Assessed by',
      assignment: 'Assignment',
      favourite: 'Favourite',
    },
    noData: 'No eConsults found',
    placeholders: {
      searchForMedicalUnit: 'Search for site',
      primarySearchFieldPlaceHolderInsuranceFlow:
        'Search for econsult id, mrn or name',
    },
  },
  casesTable: {
    noData: 'No cases found',
    tooltips: {
      cannotCombineFilters:
        'Filter cannot be combined with the selected option.',
    },
  },
  tooltips: {
    clickToAddNewAreas: 'Click the image to start adding new areas',
    dragAndDropToDisplayImage: 'Drag and drop to display image',
    editUser: 'Edit user',
    notOnSameLesionAsEditing:
      'Please note that you are not on the same lesion as you are editing',
    removeUser: 'Remove user',
    userInformationIsMissing: 'User information is missing',
    youNeedAtLeastOneAction: 'Please select at least one action',
  },
  unitUsersPage: {
    pageTitle: 'User management',
    pageDescription: 'View and edit user info within ',
    multiple: 'Multiple',
    none: 'None',
    you: '(You)',
    deactivateUser: 'Deactivate user',
    back: 'Back',
    noData: 'No Data',
    deletedReasonExpired: 'Access expired',
    deletedReasonInactive: 'Inactive for {days:number} days',
    deletedReasonInactiveGeneric: 'Due to inactivity',
    deletedReasonRemovedByUser: 'By superuser',
    expired: 'due to expired access',
    inactivity: 'due to inactivity',
    vacationEndsIn: 'Vacation ends in {nrOfDays} day{{s}}',
    vacationIn: 'Vacation in {nrOfDays} day{{s}}',
    lastActiveToday: 'Last active today',
    lastActiveDaysAgo: 'Last active {nrOfDays:number} day{{s}} ago',
    deactivatedIn: 'Deactivated in {nrOfDays:number} day{{s}}',
    downloadCSVFailed: 'Failed to download csv',
    downloadCSVSuccess: 'Successfully downloaded csv',
    usersPersonalInformation: 'User´s personal information',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phoneNumber: 'Phone number',
    npi: 'NPI',
    specialty: 'Specialisation',
    update: 'Update',
    updateNotAllowedHere:
      'Editing is only available in a medical unit you administer. Switch medical unit to edit this user.',
    userAffiliations: 'User affiliations',
    userAffiliationsDescription: "These are the user's affiliations and roles",
    roles: 'Roles',
    telespecialist: 'Telespecialist',
    resendInvitation: 'Resend invitation',
    copyInvitation: 'Copy invitation',
    deleteInvitation: 'Delete invitation',
    exportTrainingLeaderBoard: 'Export leader board',
    inactive: 'Deactivated',
    deactivatedOn: 'Deactivated on',
    reactivateUser: 'Reactivate user',
    reactivateUserSorRequired:
      'Cannot reactivate user — SOR registration is required in this federation',
    invitations: 'Invitations',
    noPendingInvitations: 'No pending invitations',
    editAffiliation: 'Edit affiliation',
    editInvitation: 'Edit invitation',
    expiryDateHelper: '* The user will have 90 days to accept your invitation.',

    deactivateUserAlert: {
      deactivate: 'Deactivate',
      deactivateUserAlertBulletPoint_1:
        'They will still be able to sign in to their account and have access to other sites',
      deactivateUserAlertBulletPoint_2:
        'They will still be visible in <>the user overview for {medicalUnitName:string}<>',
      deactivateUserAlertBulletPoint_3:
        'Their access to <>{medicalUnitName:string}<> can be restored anytime',
      deactivateUserAlertTitle: 'Deactivate user?',
      toastMessages: {
        deactivateFailure: 'Could not deactivate user',
        deactivateSuccess:
          'User with email {email:string} was successfully deactivated',
      },
      willNoLongerHaveAccessTo:
        '<>{email}<> will no longer have access to <>{medicalUnitName:string}<>',
    },

    toastText: {
      successfullyAddedUser: 'Successfully invited {email:string}',
      needEmailOrNPI: 'Need to fill out email or NPI',
      cancel: 'Cancel',
      updatedUser: 'Successfully updated user',
      removedAffiliation: 'Successfully removed role',
      somethingWentWrong: 'Something went wrong',
      copiedLink: 'Copied invitation link to clipboard',
      deletedInvitation: 'Deleted the invitation',
      resendInvitation: 'Successfully send an invitation reminder',
      tooManyInvitationReminders:
        'Too many reminders send, you can only send 1 per hour per email',
    },

    roleSheet: {
      title: 'Add role',
      role: 'Role',
      network: 'Network',
      site: 'Site',
    },

    tableHeaders: {
      name: 'Last name, First name',
      contact: 'Contact',
      association: 'Association',
      roles: 'Roles',
      role: 'Role',
      status: 'Activity status',
      added: 'Added',
      expiry: 'Expires at',
    },

    userRolesTableHeaders: {
      networkName: 'Network name',
      siteName: 'Site name',
      roles: 'Role(s)',
      occupation: 'Occupation',
      specialty: 'Specialisation',
      conditionTypes: 'Condition types',
      activityStatus: 'Activity status',
      added: 'Added',
    },

    conditionTypesField: {
      label: 'Condition types',
      helperText: "Leave empty to use the clinic's condition types.",
      useClinicsConditionTypes: "Use clinic's condition types",
      inherited: "Clinic's condition types — {names:string}",
      inheritedEmpty: "Clinic's condition types — none available",
      editFailed: 'Could not save condition types',
      optionsSource:
        'Only condition types {federation:string} offers can be chosen.',
      notOffered: '{name:string} (no longer offered by {federation:string})',
      federationFallback: 'the federation',
      loadError:
        'Could not load the condition types offered by the federation.',
    },

    filters: {
      searchForNameOrEmail: 'Search for name or email',

      networkFilter: {
        title: 'Select networks',
        allNetworks: 'All networks',
        searchPlaceholder: 'Type a network...',
        selectedNetworks: 'Selected network',
        reset: 'Reset',
        defaultTriggerText: 'All networks',
        selectedNetworksTriggerText: 'Networks',
        noValues: 'No networks',
      },

      siteFilter: {
        title: 'Select sites',
        allSites: 'All sites',
        searchPlaceholder: 'Type a site...',
        selectedSites: 'Selected sites',
        reset: 'Reset',
        defaultTriggerText: 'All sites',
        selectedSitesTriggerText: 'Sites',
        noValues: 'No sites',
      },

      statusFilter: {
        title: 'Select statuses',
        allStatuses: 'All statuses',
        selectedStatuses: 'Selected statuses',
        reset: 'Reset',
        selectedStatusesTriggerText: 'Activity status',
        noValues: 'No status',
        defaultTriggerText: 'Activity status: All',
        values: {
          active: 'Active',
          deactivated: 'Deactivated',
          aboutToBeDeactivated: 'About to be deactivated',
          unaffiliated: 'Unaffiliated',
          pendingInvitation: 'Pending Invitation',
          onVacation: 'On vacation',
          subValues: {
            unaffiliated: 'never belonged to a medical unit',
            active: 'active within the last {days:number} days',
            deactivated:
              'manually deactivated or inactive for more than {days:number} days',
            aboutToBeDeactivated:
              'inactive for {fromDays:number}-{toDays:number} days',
          },
        },
      },

      userRolesFilter: {
        title: 'Select user roles',
        searchPlaceholder: 'Type a role...',
        allRoles: 'All roles',
        selectedRoles: 'Selected roles',
        reset: 'Reset',
        selectedRolesTriggerText: 'Roles',
        defaultTriggerText: 'Roles: All',
        noResultsFound: 'No roles found',
      },

      associationFilter: {
        title: 'Select associations',
        searchPlaceholder: 'Type a network or site...',
        allAssociation: 'All associations',
        selectedAssociation: 'Selected associations',
        reset: 'Reset',
        selectedAssociationTriggerText: 'Associations',
        defaultTriggerText: 'Associations: All',
        noResultsFound: 'No associations found',
      },
    },

    expiringUsersFilter: {
      expiringSoonFilterName: 'Expires: In less than 14 days',
      filterValue: 'In less than 14 days',
      noFilterValue: 'All users',
      notExpiringSoonFilterName: 'Expires',
    },

    inviteUnitUserDialog: {
      allowedClinicalActions: 'Clinical actions',
      clinician: 'Clinician',
      email: 'Email',
      sendInvitation: 'Send invitation',
      expireDate: 'Expiration date',
      expireDateExplanation: 'obsolete',
      inviteUser: 'Invite user',
      medicalUnit: 'Site',
      medicalUnitDisabled:
        'This medical unit cannot be selected because the user is already assigned a role here.',
      npi: 'NPI',
      npiPlaceholder: 'NPI',
      occupation: 'Occupation',
      role: 'Role',
      specialty: 'Specialisation',
      jurisdictionalGroup: 'Network',

      errorMessages: {
        invalidEmail: 'Please enter a valid email with @ and a .',
        invalidOccupation: 'Please pick a valid occupation',
        invalidSpecialty: 'Please pick a valid specialisation',
        noClinicalActionPicked: 'Please pick a set of allowed clinical actions',
        noDatePicked: 'Please pick an expiration date',
        noEmailEntered: 'Please enter email',
        noOccupationPicked: 'Please pick an occupation',
        noRolePicked: 'Please pick a role',
        noSpecialtyPicked: 'Please pick a specialisation',
      },

      placeholders: {
        email: 'email@domain.com',
        medicalUnit: 'Select site',
        occupation: 'Pick occupation',
        role: 'Select role',
        specialty: 'Pick specialisation',
      },
    },

    mergeUsersDialog: {
      title: 'Merge users',
      description:
        'Move identity fields and affiliations from one user over to the main user. The user you migrate from is deactivated afterwards.',
      selectUsers: 'Select users',
      selectTargetUser: 'Select main user...',
      selectSourceUser: 'Select user to migrate from...',
      searchUsers: 'Search users...',
      noUsersFound: 'No users found',
      next: 'Next',
      back: 'Back',
      merge: 'Merge users',
      cancel: 'Cancel',
      sub: 'Sub (primary login identifier)',
      oneLoginSub: 'OneLogin Sub',
      kratosId: 'Kratos ID',
      fieldAlreadyMerged:
        'This field has already been merged from another user',
      checkingActivity: 'Checking clinical activity...',
      loadingUsers: 'Loading the users...',
      sourceUserHasActivity:
        'The user you are migrating from has clinical activity and cannot be merged. Health records: {healthRecordCount:number}, Conditions: {conditionCount:number}, Triages: {conditionTriageCount:number}',
      confirmMerge: 'Confirm merge',
      confirmMergeDescription:
        'Are you sure you want to merge these users? This action cannot be easily undone.',
      merging: 'Merging...',
      mainUser: 'Main user',
      userToMigrateFrom: 'User to migrate from',
      selectMainUserFirst: 'Select the main user first',
      createdOn: 'Created {date:string}',
      userIdShort: 'ID {id:string}',
      noMedicalUnit: 'No medical unit',
      candidateRestrictionHint:
        'Only users who share a medical unit with the main user, or belong to no medical unit at all, can be migrated.',
      selectIdsToMigrate: 'Select the ids to migrate',
      selectIdsToMigrateDescription:
        'Pick the ids to move from the user on the left over to the main user on the right. Replaced ids are struck through, and the id taking their place is shown in green. Selecting none is fine — the affiliation moves either way.',
      newValues: 'New values for the main user',
      affiliationWarningTitle: 'What moves with the merge',
      affiliationWarning:
        "The migrated user's affiliation is transferred to the main user and closed in the same step, so their record of it belongs to the main user while granting no new access. Unsubmitted drafts stay with the closed affiliation rather than moving into the main user's own. Only users from one of the main user's medical units, or with no medical unit at all, can be migrated.",
      idTooltips: {
        sub: 'The primary login identifier. Its prefix says which login method it belongs to: dkid| is MitID, 1login| is 1Login, kratos| is Kratos and epic| is Epic.',
        oneLoginSub:
          'The 1Login identifier, kept alongside the primary login so a 1Login account stays connected after the user moved to MitID.',
        kratosId: 'The identity id used by the Kratos login flow.',
      },
      toastMessages: {
        mergeSuccess: 'Users merged successfully',
        mergeFailed: 'Failed to merge users: {error:string}',
      },
    },
    reactivateUserDialog: {
      doNotSetExpirationDate: "Don't set expiration date",
      reactivate: 'Reactivate',
      specifyExpirationDate:
        'Specify the expiration date for their access to {medicalUnitName:string}',
      title: 'Reactivate user?',
      toastMessages: {
        reactivateFailed: 'Something went wrong, user was not reactivated',
        reactivateSuccess:
          'Reactivation was successful and new invitation has been sent to {email:string}',
      },
      willBeReactivatedWithPrevAccess:
        '<>{email}<> will be reactivated with clinician access to <>{medicalUnitName:string}<>',
    },
  },
  specialistGroupsPage: {
    pageTitle: 'Specialist groups',
    createNew: 'Create new',
    edit: 'Edit specialist group',
    back: 'Back',
    searchGroups: 'Search for specialist groups',
    new: 'New specialist group',
    info: 'Specialist group info',
    name: 'Name',
    description: 'Description',
    addName: 'Add name',
    addDescription: 'Add description',
    update: 'Update',
    specialists: 'Specialists',
    searchSpecialists: 'Search for specialist or clinical site',
    noSpecialistGroups: 'No specialist groups',
    addSpecialistGroup: 'Create new specialist group to see them listed here.',
    add: 'Add',
    specialist: 'Specialist',
    type: 'Type',
    allTypes: 'All types',
    email: 'Email',
    phoneNumber: 'Phone number',
    affiliations: 'Affiliations',
    allAffiliations: 'All affiliations',
    noSpecialists: 'No specialists in this group',
    addSpecialists:
      'Add specialists or clinical site, to see them listed here.',
    specialistGroupAffiliations: 'Specialist group affiliations',
    searchAffiliations: 'Search for site or network',
    noAffiliations: 'No affiliations found',
    addAffiliations:
      'Add this specialist group to one or more clinical sites or networks to see them listed here.',
    addSpecialist: 'Add specialist',
    selectedSpecialists: 'Selected specialists',
    clinicalSite: 'Clinical site',
    network: 'Network',
    preferredSpecialists: 'Preferred specialists',
    allowedSpecialists: 'Allowed specialists',
    openUserTable: 'Open in user table',
    remove: 'Remove from list',
    moreOptions: 'More options',
    nameAlreadyExists: 'A specialist group with this name already exists',
    assignmentNotificationMode: 'New-assignment emails',
    assignmentNotificationModeDescription:
      'Choose when members get an email about a newly assigned case.',
    assignmentNotificationModes: {
      EveryAssignment: {
        label: 'Every assignment',
        description: 'Email the assigned specialist about every case.',
      },
      Standard: {
        label: 'Standard',
        description:
          'Only email when the specialist has no other unanswered cases and was not emailed in the last 2 hours.',
      },
      Off: {
        label: 'Off',
        description:
          'Never email about new assignments. Reminders and reassignment warnings are still sent.',
      },
    },
    toasts: {
      created: 'Specialist group created',
      updated: 'Specialist group updated',
      specialistAssigned: 'Specialist assigned to group',
    },
  },
  medicalUnitsPage: {
    pageTitle: 'Clinical sites',
    createNew: 'Create new',
    edit: 'Edit clinical site',
    back: 'Back',
    new: 'New clinical site',
    info: 'Clinical site info',
    noSites: 'No clinical sites',
    addSites: 'Create new clinical site to see them listed here.',
    searchSites: 'Search for site name',
    name: 'Name',
    addName: 'Add name',
    jurisdictionalGroup: 'Network',
    addJurisdictionalGroup: 'Select network',
    conditionTypes: 'Condition types',
    conditionTypesCaption:
      'Shows the condition types {federation:string} offers. Only Melatech can change which types a federation offers.',
    conditionTypeNotOffered:
      'No longer offered by {federation:string}, untick to remove',
    federationFallback: 'the federation',
    conditionTypesLoadError:
      'Could not load the condition types offered by the federation.',
    defaultActions: 'Default action for new user invites',
    dermatoscope: 'Dermatoscope',
    description: 'Description',
    addDescription: 'Add description',
    onboardingDate: 'Dermloop onboarding date',
    selectDate: 'Select date',
    add: 'Add',
    seeMore: 'See more',
    seeLess: 'See less',
    otherDetails: {
      title: 'Address & Other details',
      address: 'Address',
      addAddress: 'Add address',
      city: 'City',
      addCity: 'Add city',
      state: 'State',
      addState: 'Add state',
      zipCode: 'Zip code',
      addZipCode: 'Add zip code',
      phoneNumber: 'Phone number',
      addPhoneNumber: 'Add phone number',
      fax: 'Fax',
      addFax: 'Add fax',
      email: 'Email',
      addEmail: 'Add email',
    },
    workflows: 'Condition workflows',
    lesions: {
      title: 'Lesions',
      description:
        'Lesions always enabled as default. This flow cannot be turned off.',
    },
    rashes: {
      title: 'Rashes',
      description:
        'Enable this to allow the users to register rashes and access the full rash workflow in Dermloop.',
    },
    lesionThickness: {
      title: 'Use lesion thickness',
      description:
        'Primary care providers (PCPs) are prompted to estimate the Breslow thickness if melanoma is selected as a tentative diagnosis during lesion registration.',
    },
    xdis02Messages: {
      title: 'XDIS02: Discharge summaries',
      description:
        "Sends a discharge summary to the referring physician's EHR system via the Danish health data network, when a specialist completes an assessment.",
    },
    xdis91Messages: {
      title: 'XDIS91: Correspondences',
      description:
        "Sends a correspondence to the user's EHR system with a copy of the patient journal, when the dedicated button in the Dermloop journal is clicked.",
    },
    specialistManagement: {
      title: 'Specialist management',
      description:
        'Allows configuration of how specialists handle cases at the site level.',
    },
    providerId: 'Provider ID',
    locationCode: 'Location code',
    cvr: 'CVR',
    created: 'Created on',
    noUsers: 'No users in this site',
    addUsers: 'Add users, to see them listed here.',
    update: 'Update',
    selectSpecialistGroup: 'Select specialist group',
    tootlips: {
      allowedSpecialists:
        'Allowed specialists are next in line to receive a case if no preferred specialists respond. They will receive cases only after the full rotation in the preferred group has been completed.',
      preferredSpecialists:
        'Preferred specialists are the first to receive a case. If none respond, the case moves on to the allowed specialists group.',
    },
    specialistsAlert: {
      title: 'Disable Specialist Management?',
      description:
        'Disabling specialist management will clear your selected specialist groups. Are you sure you want to continue?',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
    sorAlert: {
      title: 'Missing SOR ID',
      description:
        'A valid medical unit must have a SOR ID. Are you sure you would like to continue without one?',
      cancel: 'Cancel',
      confirm: 'Continue',
    },
    deleteSorAlert: {
      title: 'Delete SOR information?',
      description:
        'Are you sure you want to delete the SOR information from this medical unit? This will also clear the provider ID.',
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    toasts: {
      created: 'Clinical site created',
      updated: 'Clinical site updated',
    },
  },

  jurisdictionalGroupPage: {
    pageTitle: 'Networks',
    createNew: 'Create new',
    edit: 'Edit network',
    back: 'Back',
    new: 'New network',
    info: 'Network info',
    noNetworks: 'No networks',
    addNetworks: 'Create new network to see them listed here.',
    searchNetworks: 'Search for network',
    name: 'Name',
    addName: 'Add name',
    description: 'Description',
    addDescription: 'Add description',
    insurancePayer: 'Insurance payer',
    insurancePayerDescription:
      'This is the list of insurance payers linked to the network. It will be available to users with insurance management permissions.',
    insurancePayerName: 'Insurance payer name',
    newProvider: 'New provider',
    numberOfClinicalSites: 'Number of clinical sites',
    numberOfUsers: 'Number of users',
    scanner: 'ID Scanner',
    fax: 'Fax',
    addFaxNumber: 'Add fax number',
    openSite: 'Open clinical site',
    filters: {
      scannerFilter: {
        title: 'Scanner',
        defaultTriggerText: 'All scanners',
        selectedScannersTriggerText: 'Scanners',
        reset: 'Reset',
        selectedScanners: 'Selected',
        allScanners: 'All scanners',
        noValues: 'No scanners',
        searchPlaceholder: 'Search scanners',
      },
      createdOnFilter: 'Created on',
    },
    toasts: {
      created: 'Network created',
      updated: 'Network updated',
    },
    scannerLabels: {
      ssn: 'CPR Scanner',
      noScanner: 'Manual Entry',
    },
    selfReactivation: 'Self-reactivation',
    selfReactivationDescription:
      'Allow inactive users to reactivate their own accounts.',
    trustedDomainMfaSkip: 'Skip phone verification',
    trustedDomainMfaSkipDescription:
      'Skip the SMS code for users who sign in with an emailed link or code.',
    emailDomainOptionsHint:
      'These options only apply to users whose email has an allowed domain. Add a domain to turn them on.',
    allowedEmailDomain: 'Allowed email domain',
    allowedEmailDomainPlaceholder: 'allowed-domain.com',
    invalidEmailDomain: 'Invalid domain format',
    addEmailDomain: 'Add email domain',
    sendFaxOnReferralClosed:
      'Send fax when eConsult is closed by insurance manager',
    pdfDownloadEnabled: 'Show button "Download PDF"',
    displayEligiblePayers: 'Show eligible payers on the front page',
    ehrManagedActionsEnabled: 'EHR managed actions',
    ehrManagedActionsEnabledDescription:
      'When enabled, clinical actions are managed by the connected EHR system (e.g. Epic). Creating new actions directly in Dermloop is disabled without an encounter context from the EHR system.',
    save: 'Save',
  },

  warningMessages: {
    areYouSureYouWantToDeleteAnnotation:
      'Are you sure you want to delete annotation for {annotation:string}?',
    changesWillBeLost: 'Your changes will be lost. Are you sure?',
    lesionAlreadyHasAnApprovedAnnotation:
      'This training lesion already has an approved annotation. Remove all previous approvals and approve this annotation?',
    unsavedChanges: 'You have unsaved changes. Are you sure you want to leave?',
    unsavedChangesNavigation:
      'You have unsaved changes. Complete or undo them to continue.',
    youAreAboutToUnapprove:
      'You are about to unapprove this annotation. Continue?',
  },

  emailOTP: {
    title: 'Verify your email',
    enterOTP: 'Enter the verification code sent to your email',
    description: 'We have sent a verification code to: {email:string}',
    verify: 'Verify',
    sendNewCode: 'Send new code',
    otpIsRequired: 'A valid verification code is required',
    resetEmail: 'Reset email',
    toasts: {
      emailResetSuccess:
        'Email successfully reset, please provide a new email address',
      error: 'Something went wrong',
      wrongCode: 'Wrong code',
      success: 'Email verified',
      errorResending: 'Could not resend verification code',
      successResending: 'Successfully made a new verification code',
    },
  },

  vacation: {
    vacation: 'Vacation',
    helpMessage:
      'Specify your vacation here such that you will not be assigned any eConsults during your time off.',
    helpMessageAdmin:
      "Specify the user's vacation here such that they will not be assigned any eConsults during their time off.",
    noVacations: "You haven't specified any vacation yet.",
    from: 'From',
    until: 'Until',
    addVacation: 'Add vacation',
    editVacation: 'Edit vacation',
    deleteVacation: 'Delete vacation?',
    successfullyDeleted: 'Vacation deleted',
    errorDeletion: 'Error deleting vacation',
    successfullyUpdated: 'Vacation saved',
    errorUpdating: 'Error updating vacation',
    successfullyCreated: 'Vacation added',
    errorCreating: 'Error adding vacation',
    fromDateRequired: 'From date is required',
    untilDateRequired: 'Until date is required',
    dates: 'Vacation dates',
    selectDates: 'Select dates...',
    datesRequired: 'Vacation dates are required',
  },

  accountDelegation: {
    error:
      'An error occured. Close and open the dialogue again or contact support if the problems persists.',
    noValidExchangeToken: 'No valid QR code session could be found.',
    linkText: 'Open Dermloop on Desktop',
    refreshTooltip: 'Click to generate new QR code',
    refreshButtonText: 'Refresh QR code',
    generateNewQRCode: 'Generate New QR Code',
    or: 'or',
    linkOnlyTitle: 'Welcome to Dermloop',
    linkOnlyDescription:
      'Access your Dermloop account to review patient cases.',
    linkOnlyButtonText: 'Open Dermloop',
    linkOnlyFootnote: 'Dermloop will open in a new browser tab.',
    copiedToClipboard: 'Copied!',
  },

  conditionPdf: {
    title: '{name: string} report',
    conditionId: 'Case ID',
    patientId: 'Account No. (MRN)',
    name: 'Name',
    dob: 'Date of birth',
    linkTitle: 'View the report with images in full resolution here:',
    pageCount: 'Page {currentPage:number} of {totalPages:number}',
    overviewImages: 'Overview images',
    closeUpImages: 'Close-up images',
    dermoscopicImages: 'Dermoscopic images',
    images: 'Images',
    pcs: 'pcs',
    seeAll: 'See all',
    yearOld: 'year old',
    success: 'Successfully downloaded PDF',
    button: {
      downloadPdf: 'Download PDF',
      downloadAll: 'Full case record',
      downloadLatest: 'Most recent teleassessment',
    },
  },

  responseSidebar: {
    successfullyReviewed: 'Case marked as reviewed',
    footer: {
      button: {
        notReviewed: 'Mark as reviewed',
        reviewed: 'Reviewed',
        submitEvaluation: 'Submit evaluation',
      },
      notePlaceholder: {
        label: 'Add note (Optional)',
        notReviewed: 'Enter note for this case in Dermloop...',
        followupQuestionSent:
          'The note field is locked, because you sent a follow-up question.',
        reviewed:
          'The note field is locked as the case has already been reviewed.',
      },
      description: {
        notReviewed:
          'Saves your note and removes the case from your task list.',
        followupQuestionSent:
          'he case has been marked as reviewed because you sent a follow-up question.',
        reviewed:
          'The case has been marked as reviewed and removed from your task list.',
      },
    },
    responseReceipt: {
      disposition: 'Disposition',
      actionUnavailable: 'This action is no longer offered by the form',
      couldNotDisplay:
        'The specialist answer could not be displayed. Please contact support.',
    },
    specialistResponse: {
      title: 'Specialist response',
      note: 'Specialist note',
      evaluatedBy: 'Evaluated and e-signed by',
      recommendation: 'Recommendation',
      teleDiagnosis: 'Tele-diagnosis',
      secondaryDiagnosis: 'Secondary diagnosis',
      tertiaryDiagnosis: 'Tertiary diagnosis',
      differentialDiagnosis: 'Differential diagnosis',
    },
    imageQualityAssessmentResponse: {
      videoLink: 'Click here to watch a video on how to take good images',
      videoDialogTitle: 'Image improvement videos',
      dermoscopicImageVideoTitle: 'Dermoscopic image',
      overviewImageVideoTitle: 'Overview image',
      title: 'Image quality assessment',
    },
    errorMessages: {
      badEvaluationRequiresNote: 'A bad evaluation requires a comment',
    },
    followUpQuestion: {
      title: 'Ask a follow-up question to the specialist',
      placeholder:
        'If you have a follow-up question for the specialist, you can type it here and send it using the button below.',
      submitText: 'Send follow-up question',
      followUpQuestionSubmitted: 'Follow-up question submitted',
      sent: {
        title: 'Your follow-up question has now been sent to the specialist.',
        question: 'Question:',
        youWillBeNotified:
          'You will be notified in the form of a new teleassessment once the specialist has responded to your inquiry.',
      },
    },
    patientExplanation: {
      label: 'Patient-facing explanation',
      description:
        "The text is shown to the patient as explanation and advice for the dermatologist's assessment. If the field is empty, no text is sent.",
      placeholder: 'Enter explanation and advice for the patient (optional)',
    },
  },

  patientResponse: {
    supportedByDermloop: 'Supported by Dermloop',
    evaluationIntendedFor: 'This evaluation is intended for',
    dateOfAssessment: 'Date of assessment',
    dermatologistAssessment: "Dermatologist's assessment",
    explanationAndAdvice: 'Explanation and advice',
    privacyAvailableUntil:
      'For your privacy, this assessment is only available until:',
    questions: 'Questions?',
    contactMessage:
      'If you have questions, you can contact {organizationName:string}.',
    sentViaSecureLink: 'Sent via a secure link',
    verifyToViewResults: 'Verify to view your results',
    enterIdentifierToAccess:
      'Enter your {identifierType|{ssn:social security number, phoneNumber:phone number}} to securely access your results',
    enterIdentifier:
      'Enter {identifierType|{ssn:social security number, phoneNumber:phone number}}',
    linkNoLongerActive: 'This link is no longer active',
    linkExpiredMessage:
      'This link has expired to protect your privacy and data security',
    error: 'ERROR!',
    invalidIdentifier: 'Invalid identifier. Please check and try again.',
    tooManyAttempts: 'Too many attempts. Contact your clinic.',
    loading: 'Loading...',
    submit: 'Submit',
  },
  support: {
    buttonTooltip: 'Contact support',
    title: "What's on your mind?",
    description:
      'Bug reports, questions, feedback or ideas. We read everything.',
    step1Label: 'Include the case?',
    step2Label: 'Description',
    step3Label: 'How would you like to be contacted?',
    descriptionPlaceholder:
      'What are you experiencing? What do you need help with? Or do you have an idea for a new feature?',
    descriptionRequired: 'Please describe what you need help with',
    switchToDetailed: 'Go to a case and reopen support to include the case',
    notOnCasePage: "You're not on a case right now",
    yes: 'Yes',
    no: 'No',
    contactMethodEmail: 'Email',
    contactMethodPhone: 'Phone',
    emailLabel: 'Email address',
    emailPlaceholder: 'your.email@example.com',
    emailRequired: 'Please provide an email address',
    emailInvalid: 'Please provide a valid email address',
    phoneLabel: 'Phone number',
    phonePlaceholder: '+1 (833) 690-2903',
    phoneRequired: 'Please provide a phone number',
    phoneInvalid: 'Please provide a valid phone number',
    orContactDirectly: 'Or contact us directly',
    callCustomerSupport: 'Call us',
    callSupportDescription: "Call us, and we'll take care of you",
    phoneNumber: '+1 (833) 690-2903',
    openingHours: '',
    cancelButton: 'Cancel',
    sendRequestButton: 'Send request',
    confirmationTitle: 'Thanks for writing to us!',
    ticketId: 'Ticket ID:',
    confirmationMessage: 'We will get back to you as soon as possible.',
    updatesVia: "You'll hear from us via",
    updatesOn: "You'll hear from us at",
    closeButton: 'Close',
    toastError:
      "Couldn't send your message — please try again. The Dermloop team has been notified.",
  },
};

export default desktop_web;
